import type {
  Cue,
  DemoDoc,
  EventType,
  Hotspot,
  Layout,
  NormRect,
  Step,
  SwipeDir,
  WireEvent,
} from '@mado/schema';
import { onAccent, surfaceFor } from '@mado/schema/color';
import { resolveStyle } from '@mado/schema/style';
import {
  calloutHoldMs,
  dwellFor,
  GLIDE_MAX_MS,
  GLIDE_MIN_MS,
  PRESS_MS,
  resolveLinearPath,
  scrollDurationMs,
} from '@mado/schema/timeline';
import { showcasePlan } from '@mado/schema/showcase';
import {
  CAMERA_SETTLE_MS,
  cueMark,
  guideHolds,
  inkRects,
  lookLayout,
  overviewFrame,
  overviewLayout,
  panDuration,
  readingMs,
  SILENT_READ_FACTOR,
  rectOnScreen,
  sameLayout,
  sameScreen,
  desktopEntry,
  phraseAtMs,
  planPhoneTracks,
  productRadiusPx,
  spotlightRect,
  STORY_END_HOLD_MS as END_HOLD_MS,
  walkFrame,
  walkMoveDuration,
  walkZoom,
  watchEstablishHold,
  WALK_SCREEN_FADE_MS,
} from '@mado/schema/camera';
import type { PxRect } from '@mado/schema/presenter';
import { Analytics } from './analytics';
import { WatchAudio } from './audio';
import { Chrome } from './chrome';
import { applyThemeFont } from './fonts';
import { gateAnswered, openGate } from './gate';
import { stringsFor, type Strings } from './strings';
import { Gestures } from './gestures';
import { MissDetector } from './miss';
import { labelText, placeLabel } from './cue-label';
import {
  calloutCard,
  calloutFoot,
  calloutTexts,
  placeCallout,
  type CalloutText,
  type CalloutVia,
} from './callout';
import { bestUrl } from './media';
import { CHROME_CLEAR_PX, chromeBand, Overlay, projectRect, type Follow } from './overlays';
import { personalizeDoc, substitute } from './personalize';
import { Presenter } from './presenter';
import { Prefetcher, reachable } from './prefetch';
import {
  CARDS_CHROME_PX,
  CARDS_MARGIN_PX,
  SentenceCards,
  type CardsPlan,
  cardLines,
  planCards,
  type StoryPlan,
} from './cards';
import type { ShowcaseStep, ShowcaseView } from './showcase';
import { stepSizes } from './sizing';
import { Stage, cameraTransition, el, isPhoneSource, type CameraMove } from './stage';
import { Caption, captionText, edgeFill, storyOn } from './story';
import { CSS } from './styles';
import {
  EASE,
  EASE_INOUT,
  dur,
  prefersReducedMotion,
  reflow,
  tween,
  type TweenHandle,
} from './motion';
import type { Watch, StartSource, StopReason } from './watch';
import { WatchStub } from './watch-stub';
import { loadWatch, warmWatch } from './watch-load';
import type { WatchLike } from './watch-types';
import type { ScrubBar } from './scrub';

/** Below this the throw is a tap that wobbled, not a fling. px/ms. */
/** A wheel notch glides in over this long. */
const WHEEL_GLIDE_MS = 140;
const FLING_MIN_V = 0.12;
/** How long the throw keeps travelling. Tuned against a real phone, not a spec. */
const FLING_DECAY_MS = 320;
const FLING_MS_PER_PX = 1.1;
const FLING_MAX_MS = 900;
/**
 * A hard human flick tops out near 3 px/ms. Anything above that is a jittery
 * sample or synthetic input, and letting it through throws the content to the
 * end of the content in one gesture.
 */
const FLING_MAX_V = 3;

export interface PlayerOptions {
  doc: DemoDoc;
  /** Event ingest endpoint. Null disables reporting (dev harness, editor preview). */
  endpoint?: string | null;
  /** Start at a specific step — powers `?s=` deep links and docs step-linking. */
  startStep?: string | null;
  /** Editor previews force a viewport class rather than measuring the real one. */
  onStepChange?: (step: Step, index: number) => void;
  /**
   * Origin to resolve the document's root-relative `/a/...` asset URLs
   * against. Null/undefined leaves them root-relative, which is correct when
   * the player runs inside our own `/e/:slug` iframe. Required for the
   * `data-inline` embed path, where the images live in a customer's DOM. See
   * media.ts `resolveAssetUrl`.
   */
  assetBase?: string | null;
  /**
   * `'fill'`: a window or frameless demo fills its host box instead of
   * keeping the capture's aspect ratio. For a host that deliberately gives a
   * desktop capture a different shape — the share page on a portrait phone
   * gives it a tall box, and focus mode (`computeLayout`) uses the extra
   * height to show more around the target instead of letterboxing. Phone and
   * tablet frames draw a device, which keeps its shape either way.
   */
  fit?: 'fill' | null;
  /**
   * Personalisation values, already sanitized (see personalize.ts), filling
   * `{key}` / `{key|fallback}` placeholders in viewer-facing text. Absent or
   * empty means "render the document exactly as authored" — no clone, no
   * substitution pass.
   */
  vars?: Record<string, string> | null;
  /**
   * Start in watch mode by itself — but never before the player is actually
   * on screen (an IntersectionObserver gates it), and it pauses again when
   * scrolled away or the tab is hidden. Off by default; the Watch control
   * is always there regardless.
   */
  autoplay?: boolean | null;
  /**
   * Tracked-link label (`?for=acme` / `data-for`): who this link was made
   * for. Forwarded to analytics on every batch, nothing else — see
   * analytics.ts.
   */
  label?: string | null;
  /** Hub key (`?hub=hk_…` / `<mado-demo hub>`): the in-app help this demo was opened from. */
  hub?: string | null;
  /**
   * Watch mode started or stopped, for any reason (button, autoplay, a
   * viewer interacting, the end of the demo). Lets an embedding page
   * (Studio's preview controls) mirror Play/Pause without polling `playing`.
   */
  onPlayStateChange?: (playing: boolean) => void;
}

/** Wheel travel (CSS px) that counts as a deliberate scroll on a scroll target. */
const WHEEL_STEP_PX = 60;
/** How much of the player must be on screen before autoplay may begin. */
const AUTOPLAY_VISIBLE = 0.25;
/** The widest picture file a phone story asks for (device px); see `sizesFor`. */
const PHONE_MAX_FILE_W = 1920;

/**
 * How long a step change waits for the incoming picture to decode before its
 * moves start anyway. Prefetched pictures decode within a frame or two; this
 * only bounds a slow network, where the placeholder then carries the frame.
 */
const DECODE_WAIT_MS = 300;
/** Phone walkthrough: the crossfade between two screens while the camera glides on. */
const GLIDE_FADE_MS = 320;
/** How long after a look-around drag a click is still taken to be its end, not a tap. */
const LOOK_CLICK_MS = 350;
/**
 * After a step change, a press on a control is taken to be the end of the press
 * that made it (a double click, a double tap): the next step's control is drawn
 * at once, often in the same place, and the second press would skip it.
 */
const STEP_TAP_GUARD_MS = 300;
/** A resize smaller than this (sub-pixel rounding, a host nudging its box) changes nothing. */
const RESIZE_MIN_PX = 2;

/** A layer at rest. The entrance transitions below move from `from` to this. */
const REST = 'translate3d(0,0,0)';

/**
 * Click-through: the longest a step's marks and camera moves take, from the screen being in to
 * the last mark going, before the viewer is left with the control. At reading pace a line took
 * 5 to 10 s, with the viewer waiting on it (founder, 2026-10-14). Since the rings carry their
 * words (2026-10-15) this is only for a step whose rings have no words to show (a phone story's
 * caption band, a step with no line); the rest are callouts the viewer steps through.
 */
export const CLICK_TOUR_MS = 2500;

/** The factor (≤ 1) that presses a cue plan's clock into `CLICK_TOUR_MS`. */
export function tourPace(plan: {
  beats: readonly { atMs: number }[];
  cues: readonly { endMs: number }[];
}): number {
  let span = 0;
  for (const b of plan.beats) span = Math.max(span, b.atMs);
  for (const c of plan.cues) span = Math.max(span, c.endMs);
  return span > CLICK_TOUR_MS ? CLICK_TOUR_MS / span : 1;
}

/** The camera shows the whole screen: no zoom beyond the fit (`containScale`). */
function wholeScreen(l: Layout): boolean {
  return l.scale <= l.containScale * 1.005;
}

const ENTER: Record<string, { from: string; exit: string; fade: boolean }> = {
  'slide-left': { from: 'translate3d(100%,0,0)', exit: 'translate3d(-28%,0,0)', fade: false },
  'slide-right': { from: 'translate3d(-100%,0,0)', exit: 'translate3d(28%,0,0)', fade: false },
  'slide-up': { from: 'translate3d(0,100%,0)', exit: 'translate3d(0,0,0)', fade: false },
  // A measured scroll (`Transition.scroll`) is drawn by `goTo` itself; where it cannot be
  // (a phone's walk, going back), the screen slides up.
  scroll: { from: 'translate3d(0,100%,0)', exit: 'translate3d(0,0,0)', fade: false },
  scale: { from: 'scale(1.04)', exit: 'scale(.98)', fade: true },
  // The next screen grows out of the click; the old one is pushed away from
  // it. `transform-origin` is set to the click point per transition (goTo).
  reveal: { from: 'scale(.35)', exit: 'scale(1.04)', fade: true },
  fade: { from: 'translate3d(0,0,0)', exit: 'translate3d(0,0,0)', fade: true },
  none: { from: 'translate3d(0,0,0)', exit: 'translate3d(0,0,0)', fade: false },
};

/** A point in viewport CSS px — where a click landed, for the reveal's origin. */
interface Point {
  x: number;
  y: number;
}

/**
 * The spotlight's dim: the theme background, darkened, at a translucency that
 * biases the eye without turning the product's white text grey.
 */
function spotColor(hex: string, alpha = 0.34): string {
  const n = parseInt(hex.slice(1), 16);
  if (!Number.isFinite(n)) return `rgba(15,14,13,${alpha})`;
  const c = (shift: number) => Math.round(((n >> shift) & 255) * 0.55);
  return `rgba(${c(16)},${c(8)},${c(0)},${alpha})`;
}

/**
 * The narration rings' dim on a desktop: lighter than the spotlight's. With the dim held across
 * rings (`CUE_DIM_HOLD_MS`) a step's page sits under it for most of its line, and at .34 it read
 * as a grey page more than as a product (player-feel 3, 2026-10-15). The ring is drawn on the
 * undimmed picture in the hole, so its own contrast does not change. A phone story keeps .34:
 * its whole screen is small, and the dim is what finds the ring.
 */
export const DESKTOP_CUE_DIM_ALPHA = 0.26;

/**
 * Two rings closer than this hold the dim between them: the hole moves from one to the next and
 * the page is never brightened in between. The gap between two rings is the camera moving to the
 * next mark (a desktop move is at most `CAMERA_PAN_MAX_MS`, 760 ms); the dim's fade out and back
 * in (320 ms each, styles.ts `.mado-cue-b`) takes about as long, so the page never settled
 * bright: it read as a blink, not a rest (player-feel 2, the founder's "blinking", 2026-10-15).
 * A longer gap is a pause the page may brighten for.
 */
export const CUE_DIM_HOLD_MS = 800;
/** Air between a device frame, its docked bar and the card under it (`placeDock`). */
const DOCK_GAP_PX = 8;
/** Room under a docked bar for the step's card to go there, off the screen. */
const DOCK_CARD_MIN_PX = 110;
/** A docked bar's least width: Watch, Back, Next, the logo and the time on one row. */
const DOCK_BAR_MIN_W = 330;

/**
 * A callout's ring is readable where the camera is when it is all on screen at this share of the
 * zoom its own framing asks for (as the calm camera's `CALM_ZOOM_NEAR`): no move then.
 */
const CALLOUT_ZOOM_NEAR = 0.85;

export class Player {
  private readonly doc: DemoDoc;
  /** The player's own words in the document's language. */
  private readonly s: Strings;
  private readonly root: HTMLElement;
  private readonly viewport: HTMLElement;
  /** Holds the step layers. In a phone story it is clipped to the framed window (`frameWindow`). */
  private readonly win: HTMLElement;
  /** The pending end card while a story zooms out to the whole screen. */
  private endTimer = 0;
  /** The CTA pill waiting for the voice to finish (`paint`). */
  private ctaTimer = 0;
  /** The step whose edge colours the window is being filled with. */
  private fillStep = '';
  private readonly overlay: Overlay;
  private readonly chrome: Chrome;
  private readonly analytics: Analytics;
  private readonly prefetch: Prefetcher;
  private readonly live: HTMLElement;
  private readonly gestures: Gestures;
  private readonly miss: MissDetector;
  /** The real Watch, or a stand-in until `watch.js` has arrived (`mountWatch`). */
  private watch: WatchLike;
  private watchGone = false;
  /** The scrub bar, from watch.js, once Watch has played (`onPlaying`). */
  private scrub: ScrubBar | null = null;
  /** A seek is cutting into the middle of a step: no chapter card over it. */
  private seekCut = false;
  /** The showcase on screen (`playShowcase`): the step's area lifted out, its lines and labels. */
  private sc: ShowcaseView | null = null;
  private readonly audio: WatchAudio;
  private readonly presenter: Presenter;
  private readonly allowFocus: boolean;
  private readonly assetBase: string | null;
  private readonly fill: boolean;
  /** `theme.motion === 'cinematic'`: the camera moves, the spotlight lights. */
  private readonly cinematic: boolean;

  private stage!: Stage;
  private index = 0;
  private ended = false;
  private busy = false;
  private ro: ResizeObserver | null = null;
  /** The bar is under the device frame (phone and tablet frames), not over its screen. */
  private readonly docked: boolean;
  /** Under the docked bar: the step's card, when the box leaves room below the device. */
  private dockEl: HTMLElement | null = null;
  private dockRoom = false;
  private dockRo: ResizeObserver | null = null;
  /** The window's own resize (an iframe's box settling), a second way to hear of a new size. */
  private onWinResize: (() => void) | null = null;
  private io: IntersectionObserver | null = null;
  private readonly onVisibility: (() => void) | null = null;
  /** Autoplay may (re)start by itself until the viewer stops it once. */
  private autoResume = false;
  private inView = true;
  /** Callers waiting for the current transition to settle (watch mode). */
  private idle: Array<() => void> = [];
  /** `performance.now()` at which the camera has come to rest. */
  private settleUntil = 0;
  /**
   * True while an action is the player's own doing (watch mode), not the
   * viewer's: step events then carry `meta.auto`, and the interaction events
   * a finger would have produced are not emitted. See packages/schema
   * `ingest.ts`.
   */
  private auto = false;
  /** Wheel travel on a step whose target is a scroll or up/down swipe. */
  private wheelSum = 0;
  /**
   * Phone story (story.ts): the step's headline above the window and its sentences in cards under
   * it (cards.ts `planCards`), for a document that may tell one; null for any other.
   */
  private readonly caption: Caption | null;
  private readonly cards: SentenceCards | null;
  private cardsFor: {
    key: string;
    shape: number;
    headH: number;
    /** Each step's sentence cards' height, and where every step's block starts. */
    cardsH: Map<string, number>;
    top: number;
    steps: Map<string, Step>;
  } | null = null;
  /** Phone story: the camera is still walking through a step's lead-in shots. */
  private waiting = false;
  /**
   * The walk under way is a silent one (click-through, Watch muted): the step's
   * targets and tooltip are drawn and live while it plays, riding each camera
   * move (`trackOverlay`), not held back until it ends (desktop-polish F8).
   */
  private liveWalk = false;
  /** The viewer's personalisation, for the words drawn at a ring (`playCues`). */
  private readonly vars: Record<string, string>;
  /**
   * Phone walkthrough: when the camera's walk (the whole screen, then the
   * target) arrives. Watch mode speaks over the walk (`whenIdle` resolves
   * once the screen is in) and moves its cursor only after this
   * (`WatchHost.cameraWait`).
   */
  private walkUntil = 0;
  private beatTimers: number[] = [];
  /** Narration cues' marks still to show or hide (`playCues`). */
  private cueTimers: number[] = [];
  /** The walk under way (`playBeats`): its clock, so a resize can re-run what is left of it. */
  private walkClock: { stage: Stage; origin: number; startMs: number; watching: boolean } | null =
    null;
  /** The stage whose walk is already timed for Watch's voice (`watchWalk`). */
  private watchPlan: Stage | null = null;
  /** A seek's offset into its step: the next Watch walk (`openStory`) starts that far in. Used once. */
  private seekOffset = 0;
  /** Watch is starting from the end card: the `goTo` back to the start plans for Watch. */
  private watchFromEnd = false;
  /** Pending end of the press guard after a step change (`STEP_TAP_GUARD_MS`). */
  private tapGuard = 0;
  /**
   * Click-through callouts open on this step (`openCallouts`): each ring with its framing and
   * words, the one on show (`i`, -1 before the first), the line's sentences no callout showed.
   */
  private co: {
    stage: Stage;
    list: (CalloutText & { mark: Cue | null; layout: Layout })[];
    i: number;
    rest: string;
    node: HTMLElement | null;
    card: HTMLElement | null;
    timer: number;
    /** Muted Watch: advanced by the clock, no buttons, no events. */
    auto: boolean;
  } | null = null;
  /**
   * After the callouts, the instruction's line is what they did not show, and it is card `n + 1`
   * of the step (`closeCallouts`).
   */
  private sayLeft: { id: string; text: string; n: number } | null = null;
  /**
   * Back from a step's first card goes to the step before and opens it on its last card, the
   * instruction (founder, 2026-10-15: "next step back or forth"), so back and forth mirror each
   * other. The step to open that way (`cardBack`, used by `openCallouts`).
   */
  private landLast: string | null = null;

  /** Pre-mounted target during a finger-tracked swipe. */
  private pending: { stage: Stage; dir: SwipeDir; hotspot: Hotspot } | null = null;

  /** Live scroll drag, anchored to the progress the drag began at. */
  private drag: { from: number; dy: number; at: number; v: number } | null = null;
  /** Phone story, scroll step: a sideways pan, anchored to the pan it began at. */
  private panDrag: number | null = null;
  /** Phone walkthrough: the framing a look-around drag started from. */
  private lookFrom: Layout | null = null;
  /** A look-around drag just ended: the click it ends in is not a tap on a target. */
  private swallowClick = false;
  /** Phone walkthrough: the demo's one close-up zoom, per viewport size (`walkZoom`). */
  private zoomKey = '';
  private zoomVal: number | undefined;
  private fling: TweenHandle | null = null;
  /** Where the wheel glide in `fling` is heading, so the next notch adds to it. */
  private wheelTo: number | null = null;
  /** Navigation requested while a transition was in flight. */
  private queued: {
    id: string;
    transition?: string | undefined;
    viaBack: boolean;
    auto: boolean;
    origin: Point | null;
  } | null = null;

  /**
   * The step the viewer actually arrived from, one entry per forward hop —
   * a swipe, a hotspot, a scroll completing, the keyboard. Session-only,
   * never persisted (rule 3): it lives as long as this `Player` instance.
   * "Back" pops it, which is what lets a branch (a hotspot elsewhere in the
   * document order) retrace to the real previous step rather than to
   * `steps[index - 1]`, a document-order neighbour that may not be where the
   * viewer came from at all.
   */
  private history: string[] = [];
  /** The lead form is open (`showGate`): closes it unanswered. Watch waits on it (`whenIdle`). */
  private gateClose: (() => void) | null = null;
  private gateWait: Array<() => void> = [];

  constructor(host: HTMLElement, opts: PlayerOptions) {
    // Always run, even with no vars: a `{company|your team}` still has a
    // fallback to resolve for a viewer who arrived without `?company=` —
    // the one thing that may skip this is a document with no placeholder
    // syntax at all (personalizeDoc's own fast path).
    this.doc = personalizeDoc(opts.doc, opts.vars ?? {});
    this.s = stringsFor(this.doc.locale);
    this.vars = opts.vars ?? {};
    this.allowFocus = !isPhoneSource(this.doc);
    this.assetBase = opts.assetBase ?? null;
    this.fill = opts.fit === 'fill';
    // A phone or tablet frame keeps its bar under the device: the app's own bottom controls
    // ("Resume workout", "Send invoice", a tab bar) are where the bar would sit (2026-11-03).
    this.docked = this.doc.theme.frame === 'phone' || this.doc.theme.frame === 'tablet';
    this.cinematic = this.doc.theme.motion === 'cinematic';
    this.onStepChange = opts.onStepChange;
    this.onPlayStateChange = opts.onPlayStateChange ?? null;

    this.analytics = new Analytics({
      endpoint: opts.endpoint ?? null,
      demoId: this.doc.id,
      version: this.doc.version,
      label: opts.label ?? null,
      hub: opts.hub ?? null,
    });

    // `sound_toggle` is always a human action (never `auto`) — emitted
    // straight through `analytics.emit`, bypassing the `auto`-tagging wrapper
    // the step events go through.
    this.audio = new WatchAudio(
      this.doc,
      { emit: (type, meta) => this.analytics.emit(type, this.current.id, meta) },
      this.assetBase,
      () => this.watch.playing,
    );

    const style = document.createElement('style');
    style.textContent = CSS;

    this.root = el('div', 'tf');
    this.root.setAttribute('role', 'application');
    this.root.setAttribute('aria-roledescription', 'interactive demo');
    this.root.setAttribute('aria-label', this.doc.title);
    this.root.tabIndex = 0;
    this.root.style.setProperty('--mado-accent', this.doc.theme.accent);
    // Ink or cream text on accent-filled controls, whichever reads better
    // on this demo's accent (the editor warns with the same function).
    this.root.style.setProperty('--mado-on-accent', onAccent(this.doc.theme.accent));
    this.root.style.setProperty('--mado-bg', this.doc.theme.background);
    // Words set straight on the demo's background (the phone story's headline
    // and opening): ink or cream, whichever reads on it. Not `--mado-ink`,
    // which is the chrome bar's cream over its own dark scrim.
    const head = onAccent(this.doc.theme.background);
    this.root.style.setProperty('--mado-head', head);
    this.root.style.setProperty('--mado-head-dim', `color-mix(in srgb, ${head} 68%, transparent)`);
    this.root.style.setProperty('--mado-head-line', `color-mix(in srgb, ${head} 22%, transparent)`);
    // The popup surface (tooltip, end card, intro/chapter card): white and
    // near-black ink on a light demo, a raised dark neutral and cream on a
    // dark one — never mado's own rust/ink, so a customer's demo keeps its
    // own colours everywhere except the accent and the "made with mado"
    // badge. Same helper the MP4 export and the editor's settings preview
    // use, so all three agree on one demo (@mado/schema/color surfaceFor).
    const surface = surfaceFor(this.doc.theme.background);
    this.root.style.setProperty('--mado-surface', surface.surface);
    this.root.style.setProperty('--mado-surface-fg', surface.fg);
    this.root.style.setProperty('--mado-surface-fg-dim', surface.fgDim);
    this.root.style.setProperty('--mado-surface-border', surface.border);
    // Style preset (theme.style). Absent or `mado` sets nothing: the stylesheet's
    // own defaults are the mado look, so older documents are untouched.
    if (this.doc.theme.style && this.doc.theme.style !== 'mado') {
      const st = resolveStyle(this.doc.theme);
      const soft = (y: number, blur: number, a: number) => `0 ${y}px ${blur}px rgba(17,19,24,${a})`;
      const v: Record<string, string> = {
        '--mado-paper': st.paper,
        '--mado-paper-ink': st.ink,
        '--mado-paper-dim': st.dim,
        '--mado-paper-rust': st.eyebrow,
        '--mado-edge-w': `${st.edgeW}px`,
        '--mado-edge': st.edge,
        '--mado-btn-edge': this.doc.theme.accent,
        '--mado-tip-bg': st.tipBg,
        '--mado-tip-fg': st.tipFg,
        '--mado-tip-edge': st.tipEdge,
        '--mado-r-tip': `${st.rTip}px`,
        '--mado-r-card': `${st.rCard}px`,
        '--mado-r-end': `${st.rEnd}px`,
        '--mado-r-btn': `${st.rBtn}px`,
        '--mado-r-pop': `${st.rPop}px`,
        '--mado-r-mark': `${st.rMark}px`,
        '--mado-mk': 'none',
        '--mado-sh-tip': soft(8, 24, 0.18),
        '--mado-sh-card': soft(18, 40, 0.2),
        '--mado-sh-card-sm': soft(10, 24, 0.2),
        '--mado-sh-btn': 'none',
        '--mado-sh-btn-down': 'none',
        '--mado-sh-pop': soft(4, 14, 0.16),
      };
      for (const k in v) this.root.style.setProperty(k, v[k]!);
    }
    // Absent `theme.font` (every document published before fonts existed) must
    // cost nothing and change nothing: skip this entirely rather than calling
    // applyThemeFont and setting an inline style to the same system stack —
    // that would add a `style="font-family:..."` attribute that isn't there
    // today, which the zero-cost-default test in fonts.test.ts checks for.
    if (this.doc.theme.font) {
      this.root.style.fontFamily = applyThemeFont(this.doc.theme.font, this.assetBase);
    }

    const frame = el('div', `mado-frame mado-frame--${this.doc.theme.frame}`);
    const device = el('div', 'mado-device');
    device.style.display = 'flex';
    device.style.flexDirection = 'column';

    // `browser` is a neutral rounded window, deliberately without drawn
    // browser chrome: a desktop recording almost always contains the real
    // browser's own tabs and address bar, and a fake one above it reads as
    // double chrome. It also keeps the device box at exactly the capture's
    // aspect ratio, so the fit never letterboxes.

    this.viewport = el('div', 'mado-viewport');
    this.win = el('div', 'mado-window');
    device.appendChild(this.viewport);
    frame.appendChild(device);

    this.overlay = new Overlay(
      {
        // A pointer's press right after a step change is the second half of a double click or
        // tap (2026-10-14); a keyboard's (detail 0) is always meant.
        onHotspot: (h, pointer) => {
          if (!(pointer && this.tapGuard)) this.activate(h);
        },
        // A target under the bar: the chrome steps aside for this step.
        onChromeOverlap: (v) => this.chrome.setYield(v),
        chromeHeight: () => this.chrome.height(),
        // `narrationMs` is null when sound is muted, narration is off or the
        // step has no clip, so the bubble stays whenever the words are not heard.
        spokenAloud: (s) => this.watch.playing && this.audio.narrationMs(s) != null,
        // Read, not heard: the line under the tooltip and on a no-target step's guide card; after
        // the callouts, only the sentences they did not show.
        say: (s) =>
          this.sayLeft?.id === s.id
            ? this.sayLeft.text
            : substitute(s.narration?.text ?? '', opts.vars ?? {}),
        // The guide card's Next: a pointer's press right after a step change is the second half
        // of a double click (two target-less steps in a row would skip one); a key is always meant.
        onNext: (pointer) => {
          if (!(pointer && this.tapGuard)) this.stepBy(1);
        },
        progress: () => `${this.index + 1} / ${this.doc.steps.length}`,
        // Click-through: the instruction is the card after the callouts, "3 / 3"; alone, no count.
        // Watch plays itself: its tooltip has no foot.
        foot: (s) => {
          if (this.watch.playing) return null;
          const n = this.sayLeft?.id === s.id ? this.sayLeft.n : 0;
          return { count: n ? `${n + 1} / ${n + 1}` : '', back: n > 0 || this.history.length > 0 };
        },
        watching: () => this.watch.playing,
        onBack: (pointer) => {
          if (!(pointer && this.tapGuard)) this.cardBack(pointer ? 'button' : 'key');
        },
        docked: () => this.docked,
        dockCard: () => (this.dockRoom ? this.dockEl : null),
      },
      { cinematic: this.cinematic, spotColor: spotColor(this.doc.theme.background), s: this.s },
    );
    this.chrome = new Chrome(
      this.doc,
      {
        onReplay: () => this.replay(),
        onPrev: () => this.stepBy(-1),
        onNext: () => this.stepBy(1),
        onCta: (href) => this.analytics.emit('cta_click', this.current.id, { href }),
        onPlayToggle: () => this.togglePlay(),
        onSoundToggle: () => this.soundToggled(this.audio.toggle()),
        onVolume: (v) => this.onVolumeInput(v),
      },
      this.assetBase,
    );
    this.chrome.setSoundOn(!this.audio.isMuted);

    // A document with shots of a landscape source may tell a phone story;
    // whether it does is decided per step, from the viewport (`storyPlan`).
    this.overlay.radius = this.doc.theme.radius;
    this.caption = storyOn(this.doc, { w: 1, h: 2 }) ? new Caption() : null;
    this.cards = this.caption ? new SentenceCards() : null;

    this.live = el('div', 'mado-sr');
    this.live.setAttribute('aria-live', 'polite');
    this.live.setAttribute('aria-atomic', 'true');

    // The watch-mode cursor lives above the targets and below the cards, in
    // a layer of its own so an overlay rebuild never takes it with it. The
    // presenter bubble sits just above it, in the same coordinate space
    // (both layers are `.mado-overlay`, inset:0 of the viewport) — a video
    // call bubble reads as being "on top of" the pointer, not under it.
    const cursorLayer = el('div', 'mado-overlay');
    const presenterLayer = el('div', 'mado-overlay');
    this.viewport.append(
      this.win,
      this.overlay.root,
      cursorLayer,
      presenterLayer,
      this.chrome.card,
      this.chrome.replay,
    );
    if (!this.docked) this.viewport.appendChild(this.chrome.root);
    this.caption?.mount(this.viewport, this.chrome.card);
    this.cards?.mount(this.viewport, this.chrome.card);
    this.root.append(style, frame, this.live);
    host.appendChild(this.root);
    if (this.docked) this.dock();

    // watch.js also draws showcase steps: a demo with one fetches it at once.
    const showcases =
      this.doc.steps.some((s) => s.showcase) || !!this.doc.phone?.steps.some((s) => s.showcase);
    this.watch = this.mountWatch(
      cursorLayer,
      this.doc.theme.frame !== 'browser',
      !!opts.autoplay || showcases,
    );
    this.presenter = new Presenter(this.doc, presenterLayer, this.assetBase);

    this.prefetch = new Prefetcher(this.doc, (step) => this.sizesFor(step), this.assetBase);
    this.gestures = new Gestures(this.viewport, {
      onDragMove: (dx, dy, axis) => this.onDrag(dx, dy, axis),
      onDragEnd: (dir) => this.onDragEnd(dir),
      onWheel: (dy) => this.onWheel(dy),
      onPan: (dx, dy) => this.onLook(dx, dy),
      onPanEnd: () => this.onLookEnd(),
      onPinch: (ratio) => this.onPinch(ratio),
    });
    // A look-around drag that ends over a target is not a press on it.
    this.viewport.addEventListener(
      'click',
      (e) => {
        if (!this.swallowClick) return;
        this.swallowClick = false;
        e.stopPropagation();
        e.preventDefault();
      },
      { capture: true },
    );

    // "Where viewers got stuck": a tap that hit nothing is one `miss` event,
    // in asset coordinates, and a gentle nudge towards the real target. What
    // counts as one interaction is defined in miss.ts.
    this.miss = new MissDetector(
      this.viewport,
      {
        context: () =>
          this.busy || this.ended || this.chrome.introOpen || this.waiting
            ? null
            : {
                step: this.current,
                // Where the picture is on screen now — the camera may still be
                // settling — not where it is going.
                layout: this.stage.liveLayout(),
                asset: this.stage.asset,
                viewport: this.vp(),
              },
        // A step with nothing to tap — the closing screen of most demos —
        // takes a tap anywhere as "go on", exactly where Enter goes: the next
        // step, or the end card with its call to action, which a tapping
        // viewer could not otherwise reach. Not a miss: there was no target to
        // miss. Anywhere includes the letterbox, a quick second tap, and a tap
        // while a transition is still running (goTo queues it, as it does a
        // hotspot's).
        //
        // A scroll step with nothing to tap is the same: dragging (or the
        // wheel) scrolls it and reaching the end goes on, and a tap goes on
        // too. It used to be a miss, so a viewer who clicked a scrolling
        // screen on desktop — or tapped it on a phone — was stuck there,
        // nudged towards a target that did not exist. A drag is never a tap
        // (miss.ts SLOP), so scrolling is unaffected.
        onTap: (x, y) => {
          if (this.ended || this.chrome.introOpen) return false;
          const step = this.current;
          // Callouts open: a press on the lit block is its Next (founder, 2026-10-15: "If I press
          // on the highlighted block it should be accounted as button Next"); with nothing to
          // tap, a press anywhere is. Elsewhere it is a miss, and only the card's Next beats.
          const co = this.co;
          if (co && co.i >= 0 && !co.auto) {
            const m = co.list[co.i]!.mark;
            const r = m ? projectRect(m.rect, co.stage.liveLayout(), co.stage.asset) : null;
            const hit = !!r && x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
            if (!hit && step.hotspots.length) return false;
            this.calloutPress('next', 'tap');
            return true;
          }
          // A showcase's controls are not on show: a tap goes on, as on a step with nothing to tap.
          if (step.hotspots.length && !step.showcase) return false;
          const next = this.nextIdFor(step);
          if (next) this.goTo(next);
          else this.finish();
          return true;
        },
        onMiss: (p) => {
          this.analytics.emit('miss', this.current.id, { x: p.x, y: p.y });
          this.overlay.nudge();
        },
      },
      this.root,
    );

    this.applyDeviceAspect();
    const start = opts.startStep ?? this.doc.start;
    this.index = Math.max(
      0,
      this.doc.steps.findIndex((s) => s.id === start),
    );
    this.mount(this.doc.steps[this.index]!);
    this.markFirstInteractiveFrame();

    this.analytics.emit('view_start', this.current.id);
    this.analytics.emit('step_enter', this.current.id, { i: this.index });

    // A chapter on the start step is the creator's intro: shown as a cover
    // until the viewer starts. A deep link into a later step skips it.
    const intro = this.chapterAt(this.current.id);
    // `theme.cover: 'cta'` (absent = 'chapter'): the cover leads with the call to action and
    // shows even without a chapter on the start step. Without a `cta` it is the chapter cover.
    const cta = this.doc.theme.cover === 'cta' ? this.doc.cta : null;
    if ((intro || cta) && this.current.id === this.doc.start) {
      this.chrome.showIntro(
        cta ? cta.headline || cta.label : intro!.chapter.title,
        () => {
          if (intro) {
            this.analytics.emit('chapter_jump', this.current.id, { c: intro.chapter.id });
          }
          this.openStory();
        },
        // One press, one event: Watch from the cover is a `watch_start`, not
        // a `chapter_jump` and then a `watch_start`.
        () => {
          this.openStory(true);
          this.watch.start('intro');
        },
        cta ? (href) => this.analytics.emit('cta_click', this.current.id, { href }) : undefined,
      );
    } else {
      this.openStory();
    }

    this.bindKeys();
    this.bindTakeover();
    this.observeResize(host);
    // A deep link past the lead form (`?s=` a later step) meets the form first.
    const gateAt = this.doc.gate
      ? this.doc.steps.findIndex((s) => s.id === this.doc.gate!.afterStep)
      : -1;
    if (gateAt >= 0 && this.index > gateAt && this.current.id !== this.doc.start) {
      if (!gateAnswered(this.doc.id)) this.showGate(null);
    }

    if (opts.autoplay) {
      this.autoResume = true;
      this.onVisibility = () => {
        if (typeof document === 'undefined') return;
        if (document.visibilityState === 'hidden') this.watch.stop('hidden');
        else this.maybeAutoplay();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.observeVisibility();
    }
  }

  /* ── sound ────────────────────────────────────────────────────────────── */

  /**
   * The viewer's volume for watch-mode audio, 0–1 (default 1). Independent
   * of mute; held in memory for this player only, never stored (rule 3).
   * Public so an embedding page (Studio's preview) can drive it.
   */
  setVolume(v: number): void {
    this.audio.setVolume(v);
    this.chrome.setVolume(this.audio.getVolume());
  }

  getVolume(): number {
    return this.audio.getVolume();
  }

  /** The chrome slider moved: raising it while muted is a press to unmute. */
  private onVolumeInput(v: number): void {
    this.audio.setVolume(v);
    if (v > 0 && this.audio.isMuted) this.soundToggled(this.audio.toggle());
  }

  /**
   * The sound control was pressed. Unmuted while Watch tells a narrated step: the step's line
   * starts now, from its top, with its walk re-timed to it — not silence until the next step.
   */
  private soundToggled(on: boolean): void {
    this.chrome.setSoundOn(on);
    if (!on || !this.watch.playing || this.busy || this.audio.narrationMs(this.current) == null) {
      return;
    }
    this.watch.resync(() => {
      this.watchPlan = null;
      this.watchWalk(true);
      this.overlay.removeTooltip();
    });
  }

  /* ── watch mode ───────────────────────────────────────────────────────── */

  private togglePlay(): void {
    if (this.watch.playing) return this.watch.stop('pause');
    this.startWatch();
  }

  /** Shared by `togglePlay` and the public `play()`: press on the end card
   *  means "watch it again" — one press, one event, so the restart is part
   *  of the start, not a `replay` of its own. */
  private startWatch(): void {
    if (this.ended) {
      this.ended = false;
      this.chrome.setEnded(false);
      this.history = [];
      // The walk back at the start is timed for the voice, not for reading (2026-10-14).
      this.watchFromEnd = true;
      try {
        this.goTo(this.doc.start, 'fade', true);
      } finally {
        this.watchFromEnd = false;
      }
      return this.watch.start('button');
    }
    // Paused part-way through the step still on screen: play on from there, not from its top.
    const at = this.watch.position();
    if (!this.watch.playing && at && at.id === this.current.id && at.ms > 0) {
      return this.seek(at.id, at.ms);
    }
    this.watchFromHere('button');
  }

  /**
   * Start Watch on the step on screen. On the opening step, before the viewer has moved, its walk
   * already ran in click-through at reading pace and the camera rests on the control: replay it
   * on the narration's clock, as Watch from the intro cover does, or the voice plays over a
   * frame that never moves (Talentika step 1 held one framing for 10 s, 2026-10-09).
   */
  private watchFromHere(source: 'button' | 'autoplay'): void {
    // The re-timed walk is planned in `onPlaying`, once the audio knows whether this start is
    // heard: an autoplay is muted, and its first step timed for a voice no one hears hid the
    // controls on the clip's clock (2026-10-14).
    this.watch.start(source);
  }

  /**
   * Watch began on the step on screen (any step, not only the first): its walk — camera beats,
   * rings — is re-run from the top on the voice's clock, as the voice starts from the top too.
   * Left alone, a step entered in click-through kept its silent walk at reading pace while the
   * line played from 0 (2026-10-14), and a deep link's reading-pace walk held the voice back until it
   * had finished, ~7 s (2026-10-14). `glide`: the camera moves back to the opening framing rather than
   * cutting (the line starts once it is there); a cover hid the picture, so a cut is right then.
   */
  private watchWalk(glide: boolean): void {
    if (this.watchPlan === this.stage) return;
    if (this.busy) {
      // Mid-transition: the incoming step's walk is re-timed when it has arrived, before Watch's
      // own wait for idle reads the clock (`onPlaying` runs before `Watch.loop`).
      this.idle.unshift(() => {
        if (this.watch.playing) this.watchWalk(false);
      });
      return;
    }
    this.openStory(true, glide);
  }

  /** Focus left on a control that has just gone (the cover's buttons): keep the keyboard (2026-10-14). */
  private reclaimFocus(): void {
    if (typeof document === 'undefined') return;
    const a = document.activeElement;
    if (!(a instanceof HTMLElement) || !this.chrome.card.contains(a)) return;
    try {
      this.root.focus({ preventScroll: true });
    } catch {
      this.root.focus();
    }
  }

  /**
   * Start watch mode (voice + auto-advancing cursor) — the same effect as
   * pressing the chrome bar's own Watch button. No-op if already playing.
   * Public so an embedding page (Studio's preview) can drive it directly:
   * the chrome bar's controls are scaled down along with everything else at
   * preview sizes and are not meant to be the only way in.
   */
  play(): void {
    if (!this.watch.playing) this.startWatch();
  }

  /** Stop watch mode. No-op if not playing. Public — see `play()`. */
  pause(): void {
    if (this.watch.playing) this.watch.stop('pause');
  }

  /** Start over from the first step — the end card's Replay button, callable
   *  without a press on it. Public — see `play()`. */
  restart(): void {
    this.replay();
  }

  /**
   * Watch as a timeline (the scrub bar, docs/plans/showcase-and-timeline.md phase C): the steps
   * Watch plays, in order, each with when it starts and how long it takes, start to the next
   * step's start. A step Watch has already played through takes what it took; the others are
   * forecast from the same durations Watch waits on (`@mado/schema/timeline`): the dwell, the
   * cursor's glide and press, the transition. Public — see `play()`.
   */
  timeline(): { steps: { id: string; startMs: number; ms: number }[]; totalMs: number } {
    const path = resolveLinearPath(this.doc);
    const chapters = new Set((this.doc.chapters ?? []).map((c) => c.step));
    let t = 0;
    const steps = path.map((step, i) => {
      const ms = Math.round(
        this.watch.took.get(step.id) ?? this.forecastMs(step, path[i + 1], chapters),
      );
      const out = { id: step.id, startMs: t, ms };
      t += ms;
      return out;
    });
    return { steps, totalMs: t };
  }

  /** How long Watch will spend on `step`, before it has played it. */
  private forecastMs(step: Step, next: Step | undefined, chapters: Set<string>): number {
    const reduced = prefersReducedMotion();
    const narration = this.audio.narrationMs(step);
    const muted = narration == null ? this.mutedForecast(step) : null;
    let ms = dwellFor(
      step,
      chapters.has(step.id) && step.id !== this.doc.start,
      narration,
      this.presenter.durationMs(step),
      muted,
      reduced,
    );
    if (step.scroll) ms += scrollDurationMs(step);
    // The cursor's glide (its length depends on where it was: the middle of the range) and press.
    else if (step.hotspots.length && !reduced) ms += (GLIDE_MIN_MS + GLIDE_MAX_MS) / 2 + PRESS_MS;
    if (next && next.transition.type !== 'none') ms += next.transition.ms;
    return ms;
  }

  /**
   * Muted Watch's callouts for a step, before its stage exists: each card's hold, the camera's
   * moves between them left out (the step on screen is measured exactly, `mutedStepMs`).
   */
  private mutedForecast(step: Step): number | null {
    if (step.id === this.current.id || step.showcase) return this.mutedStepMs(step);
    const line = step.narration?.text.trim();
    if (!line) return null;
    const rings = (step.cues ?? []).filter((c) => c.effect !== 'look');
    if (!rings.length) return null;
    const { items, rest } = calloutTexts(
      substitute(line, this.vars),
      rings.map((c) => substitute(c.phrase, this.vars)),
    );
    let total = items.reduce((sum, it) => sum + this.autoHold(it.text), 0);
    // A phone story shows the tooltip as the headline the whole step: nothing left to read.
    const tip = step.hotspots.length && !this.told() ? (step.annotation?.text.trim() ?? '') : '';
    const left = [tip, rest.trim()].filter(Boolean).join(' ');
    if (left) total += this.autoHold(left);
    return Math.round(total);
  }

  /** Where Watch is: the step and how far into it, in ms; null before Watch has played. */
  position(): { id: string; ms: number } | null {
    const at = this.watch.position();
    // Moved on by hand since Watch stopped: the step on screen, from its top.
    if (!at || (!this.watch.playing && at.id !== this.current.id)) {
      return this.scrub ? { id: this.current.id, ms: 0 } : at;
    }
    return at;
  }

  /** The chapter a step is in: the last one opened at or before it ('' before any). */
  private chapterTitle(id: string): string {
    const i = this.doc.steps.findIndex((s) => s.id === id);
    let chapter = '';
    for (const c of this.doc.chapters ?? []) {
      const at = this.doc.steps.findIndex((s) => s.id === c.step);
      if (at >= 0 && at <= i) chapter = c.title;
    }
    return chapter;
  }

  /** The bar's label for a step: its chapter, then its number and name. */
  private stepLabel(id: string): string {
    const i = this.doc.steps.findIndex((s) => s.id === id);
    const step = this.doc.steps[i];
    const chapter = this.chapterTitle(id);
    const name = step?.name ? `${i + 1}. ${step.name}` : this.s.step(i + 1);
    return chapter ? `${chapter} · ${name}` : name;
  }

  /** The bar comes with Watch: mounted the first time Watch plays, kept after (a pause can scrub). */
  private mountScrub(): void {
    const m = globalThis.MadoWatch;
    if (this.scrub || !m?.ScrubBar) return;
    this.scrub = new m.ScrubBar(this.chrome.root, {
      timeline: () => this.timeline(),
      position: () => this.position() ?? { id: this.current.id, ms: 0 },
      seek: (id, ms) => this.seek(id, ms),
      label: (id) => this.stepLabel(id),
      words: { timeline: this.s.timeline, step: (n, label) => this.s.stepNamed(n, label) },
    });
  }

  /**
   * Play from `offsetMs` into step `stepId` (the scrub bar): the player cuts to that step, its
   * walk and callouts start from that point, its line from that point in the clip, and Watch
   * plays on from there. Public — see `play()`.
   */
  seek(stepId: string, offsetMs = 0): void {
    if (!this.doc.steps.some((s) => s.id === stepId)) return;
    const go = (): void => {
      if (this.ended) {
        this.ended = false;
        this.chrome.setEnded(false);
      }
      if (this.current.id !== stepId) {
        this.seekCut = offsetMs > 0;
        try {
          this.goTo(stepId, 'none');
        } finally {
          this.seekCut = false;
        }
      }
      this.watchPlan = null;
      this.seekOffset = Math.max(0, offsetMs);
      // Playing, the walk is re-timed here; starting, `onPlaying` does it with the same offset.
      if (this.watch.playing) this.watchWalk(false);
    };
    this.watch.seek(offsetMs, go);
  }

  /**
   * Any real input on the stage ends watch mode and is then handled exactly
   * as it would be otherwise: the tap lands, the key navigates, the wheel
   * scrolls. Capture phase, so this runs before the gesture, miss and key
   * handlers see the same event. The chrome bar is exempt — its buttons speak
   * for themselves (Pause is the one that matters).
   */
  private bindTakeover(): void {
    // Phone story: a tap while the camera walks to the target skips to it
    // (and is not a tap at the pixels — it was a tap on a picture in motion).
    this.root.addEventListener(
      'pointerdown',
      (e) => {
        if (!this.waiting) return;
        if (e.target instanceof Element && e.target.closest('.mado-chrome,.mado-card,.mado-gate'))
          return;
        // A silent walk's targets are already live: a tap on one is that tap, not a skip.
        if (this.liveWalk && e.target instanceof Element && e.target.closest('.mado-hotspot')) {
          return;
        }
        // Nothing to tap at the end of the walk: the tap is "go on" (onTap),
        // not "show me the target" — one tap, one step, as everywhere else.
        // A showcase still playing: a tap shows it whole (`skipBeats`); the next tap goes on.
        if (!this.current.hotspots.length && !this.current.showcase) return;
        this.miss.skipPress();
        this.skipBeats();
      },
      { capture: true, passive: true },
    );
    this.root.addEventListener(
      'pointerdown',
      (e) => {
        if (!this.watch.playing) return;
        if (e.target instanceof Element && e.target.closest('.mado-chrome,.mado-gate')) return;
        // The press that took over is not a tap at the pixels.
        this.miss.skipPress();
        this.watch.stop('pointer');
      },
      { capture: true, passive: true },
    );
    this.root.addEventListener(
      'keydown',
      (e) => {
        if (!this.watch.playing) return;
        // Typing in the lead form is not taking over (Watch waits on the form anyway).
        if (e.target instanceof Element && e.target.closest('.mado-gate')) return;
        // The scrub bar's own keys (Enter, arrows) seek: they are not a take-over.
        if (e.target instanceof Element && e.target.closest('.mado-seg')) return;
        if (e.target instanceof Element && e.target.closest('.mado-play,.mado-vol')) {
          // Space or Enter on a focused bar button presses it (Pause is the press that stops).
          // The volume slider's own keys move the slider. Anything else — an arrow, Home — ends
          // Watch like any key: it navigated while Watch played on, stale (2026-10-14).
          if (e.key === ' ' || e.key === 'Enter') return;
          if (e.target instanceof HTMLInputElement && e.key !== 'Escape' && e.key !== 'k') return;
        }
        // The video conventions pause and nothing more; `bindKeys` honours
        // the preventDefault. Any other key stops playback and then does
        // what it always does.
        if (e.key === ' ' || e.key === 'k' || e.key === 'Escape') e.preventDefault();
        this.watch.stop('key');
      },
      { capture: true },
    );
    this.viewport.addEventListener(
      'wheel',
      (e) => {
        if (e.target instanceof Element && e.target.closest('.mado-chrome')) return;
        if (this.watch.playing) this.watch.stop('wheel');
      },
      { capture: true, passive: true },
    );
  }

  /** Autoplay waits for the player to be on screen, and pauses when it is not. */
  private observeVisibility(): void {
    if (typeof IntersectionObserver === 'undefined') {
      this.maybeAutoplay();
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        const e = entries[entries.length - 1];
        if (!e) return;
        this.inView = e.isIntersecting && e.intersectionRatio >= AUTOPLAY_VISIBLE;
        if (this.inView) this.maybeAutoplay();
        else this.watch.stop('offscreen');
      },
      { threshold: [0, AUTOPLAY_VISIBLE] },
    );
    this.io.observe(this.root);
  }

  private maybeAutoplay(): void {
    if (!this.autoResume || !this.inView || this.watch.playing || this.ended) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    this.watchFromHere('autoplay');
  }

  /**
   * Watch lives in `watch.js`, a second file. If it is already there (cached, or loaded first) the
   * real class is built now; else a stand-in keeps click-through working, the file is fetched
   * when the page is idle (at once for autoplay or a press on Play) and the real class takes over,
   * with the request that asked for it.
   */
  private mountWatch(layer: HTMLElement, touch: boolean, eager: boolean): WatchLike {
    const make = (m: NonNullable<typeof globalThis.MadoWatch>): Watch =>
      new m.Watch(this.watchHost(), layer, touch);
    if (globalThis.MadoWatch) return make(globalThis.MadoWatch);
    const stub: WatchStub = new WatchStub(() => {
      void loadWatch().then(
        (m) => {
          if (this.watchGone || this.watch !== stub) return;
          const real = make(m);
          this.watch = real;
          stub.replay(real);
        },
        () => {},
      );
    });
    if (eager) void loadWatch().catch(() => {});
    else warmWatch();
    return stub;
  }

  /** What watch mode is allowed to do to the player — every path a finger has. */
  private watchHost(): ConstructorParameters<typeof Watch>[0] {
    // Getters on an object literal cannot be arrow functions, and `this`
    // inside them is the literal — hence `self`.
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return {
      get current() {
        return self.current;
      },
      get ended() {
        return self.ended;
      },
      get layout() {
        return self.stage.currentLayout;
      },
      get asset() {
        return self.stage.asset;
      },
      get viewport() {
        return self.vp();
      },
      whenIdle: () => this.whenIdle(),
      gated: () => this.gateClose !== null,
      cameraWait: () => Math.max(0, this.walkUntil - performance.now()),
      chapterFlashing: () => this.chrome.cardOpen && !this.chrome.introOpen,
      auto: (fn) => this.asAuto(true, fn),
      advance: (next) => (next ? this.goTo(next) : this.finish()),
      activate: (h) => this.activate(h),
      drag: (dx, dy, axis) => this.onDrag(dx, dy, axis),
      dragEnd: (dir) => this.onDragEnd(dir),
      scrollProgress: () => this.stage.scrollProgress,
      setScrollProgress: (p) => {
        this.fling?.cancel();
        this.fling = null;
        if (this.waiting) this.skipBeats();
        this.stage.setScrollProgress(p);
        this.overlay.reposition(this.current, this.stage.currentLayout, this.stage.asset);
      },
      scrollRange: () => this.scrollRange(),
      settleScroll: () => this.settleScroll(),
      type: (h, done) => {
        if (!(h.gesture === 'type' && this.current.input?.target === h.id)) return false;
        return this.overlay.beginTyping(
          this.current,
          this.stage.currentLayout,
          this.stage.asset,
          done,
        );
      },
      interrupted: () => {
        // A swipe caught mid-stroke springs back; typing caught mid-word is
        // removed and the target redrawn so the viewer starts it clean.
        if (this.pending) this.onDragEnd(null);
        if (this.overlay.cancelTyping()) this.paint();
      },
      emit: (type, meta) => this.analytics.emit(type, this.current.id, meta),
      narrationMs: (step) => this.audio.narrationMs(step),
      playNarration: (step, offset) => this.audio.playNarration(step, offset),
      playSfx: (name) => this.audio.playSfx(name),
      presenterMs: (step) => this.presenter.durationMs(step),
      showPresenter: (step) => this.presenter.show(step),
      hidePresenter: () => this.presenter.hide(),
      mutedMs: (step) => this.mutedStepMs(step),
      onPlaying: (on, why) => {
        const covered = this.chrome.introOpen;
        if (on) this.reclaimFocus();
        if (on) this.mountScrub();
        if (on && this.chrome.introOpen) this.chrome.hideCard();
        // Autoplay may pick up again after the page took it away (hidden,
        // scrolled off) — never after the viewer stopped it, and not after
        // the demo ran to its end.
        if (!on && why !== 'hidden' && why !== 'offscreen') this.autoResume = false;
        this.chrome.setPlaying(on);
        // Audio and the visual stop/start happen on the same beat — see
        // audio.ts `notifyWatchStart`/`notifyWatchStop`.
        if (on) this.audio.notifyWatchStart(why as StartSource);
        else this.audio.notifyWatchStop(why as StopReason);
        this.chrome.setSoundOn(!this.audio.isMuted);
        // After the audio knows whether this start is heard (an autoplay is muted).
        if (on) this.watchWalk(!covered);
        else this.watchStopped(why as StopReason);
        // Watch began on a step already drawn with its bubble; the voice now says it.
        if (on) this.placeCta(this.current);
        if (on && this.audio.narrationMs(this.current) != null) this.overlay.removeTooltip();
        else this.overlay.redrawGuide(this.current, this.stage.currentLayout, this.stage.asset);
        this.onPlayStateChange?.(on);
      },
    };
  }

  /** Resolves once no layer transition is in flight and the camera has settled. */
  private whenIdle(): Promise<void> {
    // The lead form holds Watch where it is; it goes on once the form is answered.
    if (this.gateClose) {
      return new Promise((r) => this.gateWait.push(() => void this.whenIdle().then(r)));
    }
    if (this.busy) return new Promise((r) => this.idle.push(() => void this.whenIdle().then(r)));
    const wait = this.settleUntil - performance.now();
    return wait > 0 ? new Promise((r) => setTimeout(r, wait)) : Promise.resolve();
  }

  /** Run `fn` with the `auto` flag set (or cleared), restoring it after. */
  private asAuto<T>(on: boolean, fn: () => T): T {
    const prev = this.auto;
    this.auto = on;
    try {
      return fn();
    } finally {
      this.auto = prev;
    }
  }

  /** Step events: tagged `auto` when the player, not the viewer, made the move. */
  private emit(type: EventType, stepId: string, meta?: WireEvent['meta']): void {
    this.analytics.emit(type, stepId, this.auto ? { ...(meta ?? {}), auto: true } : meta);
  }

  /**
   * A `performance.mark` for "first step image decoded and its hotspot
   * focusable" — PRODUCT-PLAN §5.4's time-to-first-interactive-frame budget
   * (< 1.5s on simulated 4G), CI-measured by `tests/e2e/tests/perf.spec.ts`
   * via CDP network throttling rather than full Lighthouse (which already
   * covers the mobile-95 / CLS-0 half of the same budget against the
   * reference demo).
   *
   * Hotspots are already focusable the instant `paint()` runs inside
   * `mount()` above — overlay geometry doesn't wait on the image. The only
   * real gate left is the first step's own `<img>` finishing decode.
   */
  private markFirstInteractiveFrame(): void {
    const mark = (): void => {
      if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
        performance.mark('tf:first-interactive-frame');
      }
    };
    const img = this.stage.layer.querySelector('img');
    if (!img || img.complete) {
      mark();
      return;
    }
    if (typeof img.decode === 'function') {
      img.decode().then(mark, mark);
    } else {
      img.addEventListener('load', mark, { once: true });
      img.addEventListener('error', mark, { once: true });
    }
  }

  private onStepChange: PlayerOptions['onStepChange'];
  private readonly onPlayStateChange: PlayerOptions['onPlayStateChange'] | null;

  /* ── geometry ─────────────────────────────────────────────────────────── */

  /**
   * Give the device box the capture's aspect ratio up front, so the embed never
   * shifts layout when the first image decodes (CLS = 0 is a CI gate).
   */
  private applyDeviceAspect(): void {
    const { captureW, captureH } = this.doc.source;
    const device = this.viewport.parentElement!;
    const { frame } = this.doc.theme;
    if (frame === 'none' || (this.fill && frame === 'browser')) {
      device.style.width = '100%';
      device.style.height = '100%';
      return;
    }
    device.style.aspectRatio = `${captureW} / ${captureH}`;
    device.style.width = '100%';
    device.style.height = 'auto';
    device.style.maxHeight = '100%';
    device.style.maxWidth = '100%';
  }

  private get current(): Step {
    return this.doc.steps[this.index]!;
  }

  private vp(): { w: number; h: number } {
    return { w: this.viewport.clientWidth, h: this.viewport.clientHeight };
  }

  /* ── mounting & navigation ────────────────────────────────────────────── */

  private mount(step: Step): void {
    // A fling outlives the step that started it unless it is stopped here: its
    // frames keep repositioning the overlay, and once the step has changed that
    // means redrawing the new step's hotspots against the old step's geometry,
    // mid-transition. The result is a step that intermittently has no targets.
    this.fling?.cancel();
    this.fling = null;
    this.drag = null;

    this.stage = this.newStage(step);
    this.win.appendChild(this.stage.layer);
    this.paint();
    this.warmAhead(step);
  }

  /**
   * Redraw everything that depends on current geometry. `settleMs` is how
   * long the camera is still travelling: the spotlight and tooltip wait for it.
   */
  private paint(settleMs = 0): void {
    const step = this.current;
    this.showCaption(step);
    // Mid-walk the picture is on a lead-in shot; the targets belong where it ends.
    // A silent walk draws them where the camera is going now (`trackOverlay`).
    const layout =
      this.waiting && !this.liveWalk ? this.stage.targetLayout() : this.stage.currentLayout;
    this.overlay.render(step, layout, this.stage.asset, { settleMs });
    // Kept in place even while hidden, so it is already positioned the
    // instant watch mode fades it in — see presenter.ts `layout`.
    if (this.presenter.active) this.presenter.layout(this.vp(), this.presenterAvoid(step, layout));
    this.chrome.setProgress(this.index, this.doc.steps.length);
    this.chrome.setBackable(this.history.length > 0);
    this.gestures.configure({
      // A phone-story scroll wider than the picture also pans sideways.
      allowX: !!this.swipeHotspot('left') || !!this.swipeHotspot('right') || this.stage.pans,
      // Vertical input also drives a step whose target is a scroll or an
      // up/down swipe: a wheel or a drag, not only a click on the target.
      allowY: !!step.scroll || !!this.verticalHotspot('up') || !!this.verticalHotspot('down'),
      // Phone walkthrough: any other drag looks around, two fingers pinch.
      pan: !step.scroll && !!this.storyPlan(step),
    });
    this.wheelSum = 0;
    this.announce(step);
    this.onStepChange?.(step, this.index);

    this.placeCta(step);
  }

  /** Does the CTA pill belong on this step: the one the creator chose ("after step"), or the last? */
  private ctaHere(step: Step): boolean {
    const cta = this.doc.cta;
    if (!cta) return false;
    return cta.afterStep === 'last' ? this.nextIdFor(step) === null : step.id === cta.afterStep;
  }

  /**
   * The step's callouts and instruction are the last word of the demo's last step: the CTA that
   * rides on the end card (afterStep 'last') stays away until they are done (2026-10-16: on a
   * phone "Create a demo" sat over the last callout's card and the step's screen). The end card
   * carries it right after. A CTA the creator put after a chosen step is theirs and stays.
   */
  private holdLastCta(): void {
    const cta = this.doc.cta;
    if (!cta || cta.afterStep !== 'last' || !this.ctaHere(this.current)) return;
    if (this.ctaTimer) window.clearTimeout(this.ctaTimer);
    this.ctaTimer = 0;
    this.chrome.hideCta();
  }

  /**
   * The CTA pill on its step; the end card carries it too once the demo is
   * finished. In Watch it comes when the step's line is over, not over it
   * (the narration is still asking for attention).
   */
  private placeCta(step: Step): void {
    if (this.ctaTimer) window.clearTimeout(this.ctaTimer);
    this.ctaTimer = 0;
    if (!this.ctaHere(step)) return this.chrome.hideCta();
    // A phone story fills the phone with the step; the end card brings the CTA right after
    // (founder's phone, 2026-10-22: "Create a demo" sat on the last showcase's panel).
    if (this.doc.cta?.afterStep === 'last' && this.storyPlan(step)) {
      return this.chrome.hideCta();
    }
    const show = (): void => {
      this.ctaTimer = 0;
      this.chrome.showCta((href) => this.analytics.emit('cta_click', step.id, { href }));
      this.avoidCta();
    };
    // Heard or not (muted, it is still the pace the step is told at).
    const clip = step.narration?.audio ? this.doc.audioAssets?.[step.narration.audio] : undefined;
    const text = step.narration?.text.trim();
    const spoken = !this.watch.playing
      ? null
      : clip
        ? clip.durationMs
        : text
          ? readingMs(text) * SILENT_READ_FACTOR
          : null;
    if (spoken != null && spoken > 0) {
      this.chrome.hideCta();
      this.ctaTimer = window.setTimeout(show, spoken);
    } else show();
  }

  /** The CTA sits clear of the step's instruction (tooltip or guide card), above it if need be. */
  private avoidCta(): void {
    const boxes = [...this.overlay.root.querySelectorAll('.mado-tooltip')].map((n) =>
      n.getBoundingClientRect(),
    );
    this.chrome.avoidCta(boxes);
  }

  /**
   * Rects the presenter bubble should keep clear of, in viewport CSS px: the
   * chrome bar's own band (unconditionally — "kept clear of the chrome bar"
   * is not a per-step judgement) and the step's target/tooltip and a story's
   * caption band, both only when this step actually has one. Best-effort:
   * `@mado/schema/presenter` `presenterBubble` falls back to the author's own
   * corner when every corner collides with something here.
   */
  /** The band at the bottom the chrome bar covers (`chromeBand`). */
  private barBand(): number {
    return chromeBand(this.chrome.height());
  }

  private presenterAvoid(step: Step, layout: Layout): PxRect[] {
    const vp = this.vp();
    const rects: PxRect[] = [];
    if (vp.h > 0) {
      rects.push({ x: 0, y: Math.max(0, vp.h - CHROME_CLEAR_PX), w: vp.w, h: CHROME_CLEAR_PX });
    }
    const target = spotlightRect(step);
    if (target) rects.push(projectRect(target, layout, this.stage.asset));
    const plan = this.storyPlan(step);
    if (plan) rects.push(plan.caption);
    return rects;
  }

  private announce(step: Step): void {
    this.live.textContent = `${this.s.stepOf(this.index + 1, this.doc.steps.length)}. ${
      step.name || step.alt || ''
    }`;
  }

  private chapterAt(
    stepId: string,
  ): { chapter: DemoDoc['chapters'][number]; index: number } | null {
    const index = this.doc.chapters.findIndex((c) => c.step === stepId);
    return index < 0 ? null : { chapter: this.doc.chapters[index]!, index };
  }

  private nextIdFor(step: Step): string | null {
    if (step.next) return step.next;
    const withTarget = step.hotspots.find((h) => h.next);
    return withTarget?.next ?? null;
  }

  private activate(h: Hotspot): void {
    if (!this.auto) this.analytics.emit('hotspot_click', this.current.id, { hotspot: h.id });
    const target = h.next ?? this.current.next;
    if (!target) return this.finish();
    // The reveal grows from the control that was pressed — its centre, the
    // same point for a finger, a mouse, watch mode and the MP4 export.
    const r = projectRect(h.geom, this.stage.liveLayout(), this.stage.asset);
    this.goTo(target, undefined, false, { x: r.x + r.w / 2, y: r.y + r.h / 2 });
  }

  /**
   * @param viaBack Set only by {@link goBack}. A back-navigation pops
   * `history` itself, so the hop it causes must not be pushed back onto it —
   * that would turn "go back one" into "go back to where you just were."
   */
  /**
   * @param origin Where the click that caused this landed, in viewport px —
   * the point a `reveal` grows from. Null (keyboard, replay) means the centre.
   */
  goTo(
    stepId: string,
    transitionOverride?: string,
    viaBack = false,
    origin: Point | null = null,
  ): void {
    const to = this.doc.steps.findIndex((s) => s.id === stepId);
    if (to < 0) return;
    if (this.gateClose) return;
    if (!viaBack && !this.busy && this.gateDue(to)) {
      return this.showGate(() => this.goTo(stepId, transitionOverride, viaBack, origin));
    }

    // Mid-transition input is queued, never dropped. Swallowing a tap because an
    // animation is still running is the classic way a player feels cheap; a
    // viewer moving faster than the transition should still land where they
    // aimed. Only the most recent intent is kept, so mashing cannot build a
    // backlog of steps to replay.
    if (this.busy) {
      this.queued = {
        id: stepId,
        transition: transitionOverride,
        viaBack,
        auto: this.auto,
        origin,
      };
      return;
    }

    const from = this.current;
    if (!viaBack) this.history.push(from.id);
    // Forward, a step starts at its first card again (only a card's Back lands on the last).
    if (!viaBack) this.landLast = null;
    this.emit('step_exit', from.id, { i: this.index });
    this.leaveStep();

    const toStep = this.doc.steps[to]!;
    let type = transitionOverride ?? toStep.transition.type;
    let spec = ENTER[type] ?? ENTER.fade!;
    let ms = dur(toStep.transition.ms);

    const outgoing = this.stage;
    this.index = to;
    this.ended = false;
    this.chrome.setEnded(false);

    const incoming = this.newStage(this.current, from);
    // Warm what can follow this step now, while the viewer reads it.
    this.warmAhead(this.current);
    const vp = this.vp();
    /** How long the camera is still moving once the moves begin. */
    let settleMs = 0;
    /** What the overlay mirrors so taps land on the moving picture (Overlay.follow). */
    const follow: Follow = {};
    /** Starts every move at once — after the incoming picture has decoded. */
    let begin: () => void;
    /** Wait for the incoming image before the moves start (a different picture only). */
    let decodeMs = DECODE_WAIT_MS;

    // One screen, two framings — the camera pans. No layer transition: the
    // same pixels are already on the new layer, at the framing the old one is
    // at this instant (mid-move included), and travel from there.
    const pan =
      this.cinematic &&
      !outgoing.isScroll &&
      !incoming.isScroll &&
      outgoing.step.asset === toStep.asset;

    // Phone story: the step opens on its first shot and walks to its target.
    const beats = incoming.beats && incoming.beats.length > 1 ? incoming.beats : null;
    this.stopBeats();
    this.stopCues();
    /** Phone walkthrough (docs/plans/phone-walkthrough.md, phone-overview.md). */
    const walk = this.walkZoom() !== undefined && incoming.framed;
    /** A new screen on a phone dissolves in at its own first framing; nothing zooms meanwhile. */
    const dissolve =
      walk &&
      !viaBack &&
      !outgoing.isScroll &&
      !incoming.isScroll &&
      !this.sameScreenAs(from, toStep);
    /** A camera move's length: a phone's pace in the walkthrough, a desktop pan's elsewhere. */
    const moveMs = (a: Layout, b: Layout): number =>
      dur(walk ? walkMoveDuration(a, b, vp) : panDuration(a, b, vp));

    // Phone walkthrough: the next screen comes in at the framing the camera is
    // at now, and the camera glides on to its target over both pictures as
    // they crossfade — one continuous look at the product, never a pull back
    // to the whole screen in between (docs/plans/phone-walkthrough.md).
    const glide =
      !pan &&
      !beats &&
      !viaBack &&
      !dissolve &&
      walk &&
      outgoing.framed &&
      incoming.framed &&
      !outgoing.isScroll &&
      !incoming.isScroll &&
      outgoing.asset.w === incoming.asset.w &&
      outgoing.asset.h === incoming.asset.h &&
      !prefersReducedMotion();

    // A screen further down the same page (`Transition.scroll`, measured at draft): the page's
    // column scrolls up by the measured distance under a still sidebar and header, so it reads
    // as a scroll, not a new screen (founder, 2026-10-12). The old column (a copy) and the new
    // one move together, pixel for pixel; the old screen underneath keeps what does not scroll.
    const sc = toStep.transition.type === 'scroll' ? toStep.transition.scroll : undefined;
    const scrolls =
      !!sc &&
      !glide &&
      !pan &&
      !viaBack &&
      !walk &&
      !outgoing.isScroll &&
      !incoming.isScroll &&
      outgoing.asset.w === incoming.asset.w &&
      outgoing.asset.h === incoming.asset.h &&
      ms > 0 &&
      // Only from the whole screen: a zoomed camera puts the column, the header and the sidebar
      // at a scale the clipped copies cannot match, and a menu open over the column rides along
      // in the copy (the Actions menu drawn twice, 2026-10-13). A zoomed step fades instead.
      wholeScreen(outgoing.liveLayout()) &&
      !prefersReducedMotion();

    if (scrolls && sc) {
      const cam = outgoing.liveLayout();
      const a = incoming.asset;
      const d = sc.shift * a.h * cam.scale;
      const left = Math.max(0, cam.tx + sc.column.x * a.w * cam.scale);
      const right = Math.max(0, vp.w - (cam.tx + (sc.column.x + sc.column.w) * a.w * cam.scale));
      const top = Math.max(0, cam.ty + sc.top * a.h * cam.scale);
      const clip = `inset(${top}px ${right}px 0px ${left}px)`;
      const ghost = outgoing.layer.cloneNode(true) as HTMLElement;
      ghost.style.clipPath = clip;
      ghost.style.transform = REST;
      incoming.setCamera(cam);
      incoming.layer.style.clipPath = clip;
      incoming.layer.style.transform = `translate3d(0,${d}px,0)`;
      incoming.layer.style.opacity = '0';
      this.win.appendChild(ghost);
      this.win.appendChild(incoming.layer);
      reflow(incoming.layer);
      begin = () => {
        const t = `transform ${ms}ms ${EASE_INOUT}`;
        incoming.layer.style.opacity = '1';
        incoming.layer.style.transition = t;
        incoming.layer.style.transform = REST;
        ghost.style.transition = t;
        ghost.style.transform = `translate3d(0,${-d}px,0)`;
        window.setTimeout(() => {
          ghost.remove();
          incoming.layer.style.clipPath = '';
        }, ms + 20);
      };
    } else if (glide) {
      const start = outgoing.liveLayout();
      // The same screen: the camera carries on from where it is to this
      // step's framing — its target, or the whole screen for an overview step.
      const target = incoming.targetLayout();
      const fadeMs = Math.max(ms, GLIDE_FADE_MS);
      ms = fadeMs;
      incoming.setCamera(start);
      incoming.layer.style.opacity = '0';
      this.win.appendChild(incoming.layer);
      reflow(incoming.layer);
      settleMs = sameLayout(start, target) ? 0 : moveMs(start, target);
      const move = { ms: settleMs, ease: EASE_INOUT };
      if (settleMs > 0)
        follow.cam = { from: start, to: target, transition: cameraTransition(move) };
      begin = () => {
        const t = `opacity ${fadeMs}ms ${EASE}`;
        incoming.layer.style.transition = t;
        incoming.layer.style.opacity = '1';
        outgoing.layer.style.transition = t;
        outgoing.layer.style.opacity = '0';
        if (settleMs > 0) {
          incoming.setCamera(target, move);
          outgoing.setCamera(target, move);
        }
        this.watch.cameraPan(settleMs, EASE_INOUT);
      };
    } else if (pan) {
      const start = outgoing.liveLayout();
      const target = beats ? beats[0]!.layout : incoming.targetLayout();
      ms = 0;
      decodeMs = 0;
      incoming.setCamera(start);
      this.win.appendChild(incoming.layer);
      reflow(incoming.layer);
      settleMs = sameLayout(start, target) ? 0 : moveMs(start, target);
      const move = { ms: settleMs, ease: EASE_INOUT };
      if (settleMs > 0)
        follow.cam = { from: start, to: target, transition: cameraTransition(move) };
      begin = () => {
        incoming.setCamera(target, move);
        outgoing.layer.style.opacity = '0';
        // The watch-mode cursor is resting on the control it pressed; it rides
        // along with the pan rather than being left behind on the glass.
        this.watch.cameraPan(settleMs, EASE_INOUT);
      };
    } else {
      // Phone walkthrough: a new screen is a dissolve, whatever the step's
      // own transition — no slide or reveal moving the picture on a phone.
      if (dissolve) {
        spec = ENTER.fade!;
        type = 'fade';
        ms = Math.max(ms, dur(WALK_SCREEN_FADE_MS));
      }
      incoming.layer.style.transform = spec.from;
      const o = type === 'reveal' ? (origin ?? { x: vp.w / 2, y: vp.h / 2 }) : null;
      if (o) {
        incoming.layer.style.transformOrigin = `${o.x}px ${o.y}px`;
        outgoing.layer.style.transformOrigin = `${o.x}px ${o.y}px`;
      }
      // Hidden until it has decoded, whatever the transition: a slide or a
      // cut must not carry an undrawn picture onto the glass. A fade starts
      // from here anyway; anything else is shown again, at once, in `begin`.
      incoming.layer.style.opacity = '0';
      // A screen change: the old screen pulls back to whole as it leaves, the
      // new one arrives whole and settles on its target once it is mostly in.
      // The reveal is its own zoom — a settle on top would be two moves.
      const settles =
        !beats && !walk && this.cinematic && type !== 'reveal' && !prefersReducedMotion();
      const target = incoming.targetLayout();
      if (beats) incoming.setCamera(beats[0]!.layout);
      if (settles) {
        const rest = incoming.restLayout();
        if (!sameLayout(rest, target)) incoming.setCamera(rest);
      }
      this.win.appendChild(incoming.layer);
      reflow(incoming.layer);

      const t = `transform ${ms}ms ${EASE}, opacity ${ms}ms ${EASE}`;
      const rested = incoming.currentLayout;
      let camMove: CameraMove | null = null;
      if (settles && !sameLayout(rested, target)) {
        const delay = Math.round(ms * 0.5);
        camMove = { ms: CAMERA_SETTLE_MS, delay, ease: EASE_INOUT };
        settleMs = delay + CAMERA_SETTLE_MS;
        follow.cam = { from: rested, to: target, transition: cameraTransition(camMove) };
      }
      if (ms > 0 && spec.from !== REST) {
        follow.layer = {
          from: spec.from,
          to: REST,
          origin: o ? `${o.x}px ${o.y}px` : '50% 50%',
          transition: `transform ${ms}ms ${EASE}`,
        };
      }
      begin = () => {
        if (!spec.fade) {
          incoming.layer.style.opacity = '1';
          reflow(incoming.layer);
        }
        if (settles) {
          const outRest = outgoing.restLayout();
          if (!sameLayout(outgoing.currentLayout, outRest)) {
            outgoing.setCamera(outRest, { ms, ease: EASE });
          }
        }
        incoming.layer.style.transition = t;
        incoming.layer.style.transform = REST;
        incoming.layer.style.opacity = '1';
        outgoing.layer.style.transition = t;
        outgoing.layer.style.transform = spec.exit;
        if (spec.fade) outgoing.layer.style.opacity = '0';
        if (camMove) incoming.setCamera(target, camMove);
      };
    }

    // The walk is timed from now, and the moves wait for the picture to
    // decode (up to `decodeMs`): start it after that bound, never before.
    // Watch mode holds a new screen over its narration's lead-in.
    const onScreen = Math.max(ms, settleMs) + decodeMs;
    const walks = !!beats || incoming.cued;
    const watching = this.watch.playing || this.watchFromEnd;
    const t0 = performance.now();
    if (walks) settleMs = this.playBeats(incoming, onScreen, watching);
    if (watching) this.watchPlan = incoming;
    /** Watch mode speaks over the beats (a phone's walk, a desktop's guided step). */
    const speakOver = (!!beats || incoming.cued) && (walk || !incoming.framed);

    this.busy = true;
    const arrives = Math.max(ms, settleMs);
    this.settleUntil = performance.now() + arrives + (beats ? 0 : decodeMs);
    this.walkUntil = this.settleUntil;
    // Watch mode speaks over a walkthrough's establishing shot: it may start
    // once the screen is in; its cursor waits for the walk (`cameraWait`).
    if (speakOver) this.settleUntil = performance.now() + onScreen;
    this.stage = incoming;
    // The overlay is drawn where the picture comes to rest; `follow` carries
    // it from where the picture starts. A silent walk rests on its opening
    // framing until its first move (`trackOverlay` takes over then): drawn at
    // the end of the walk instead, the live targets sat where the controls
    // were not, invisibly taking presses through a pan (2026-10-14).
    // Callouts the same: each one's move takes the targets on from there (`showCallout`).
    const live = this.liveWalk || this.co?.stage === incoming;
    incoming.aim(live && beats ? beats[0]!.layout : incoming.targetLayout());
    // Overlay is rebuilt immediately so the incoming step is interactive as soon
    // as it is visible, rather than after the animation settles. Cinematic
    // keeps it unseen until the picture has arrived (layer move included);
    // static shows it at once, as it always has. A story's lead-in shots
    // always wait: the target is not on screen until the last one. Either way
    // it rides along with the picture while it moves (`follow`), so a tap
    // mid-move lands on the control the viewer can see — hidden while a
    // story walks (`mado-overlay--wait`), and the follow ends at the rest
    // layout the overlay is drawn at.
    this.paint(this.cinematic || beats ? arrives : 0);
    const moveOverlay = this.overlay.follow(follow);
    this.emit('step_enter', this.current.id, { i: this.index });
    if (!viaBack && !this.seekCut) {
      const ch = this.chapterAt(this.current.id);
      if (ch) this.chrome.flashChapter(ch.chapter.title, ch.index);
    }

    // The moves start once the new picture is decoded (bounded wait), so a
    // step never arrives as a blank frame. Input meanwhile is queued (busy).
    incoming.whenDecoded(decodeMs, () => {
      begin();
      moveOverlay();
      // The walk was timed for the longest decode wait; the picture is here sooner (cached, as
      // a prefetched step's is): re-time it from now, so the rings and the voice (which starts
      // after the moves) share one clock rather than the rings coming ~300 ms late (2026-10-14).
      let walkArrives = arrives;
      const early = decodeMs - (performance.now() - t0);
      if (walks && early > 16 && this.stage === incoming && this.walkClock?.stage === incoming) {
        this.stopBeats();
        this.stopCues();
        incoming.clearCues();
        walkArrives = Math.max(ms, this.playBeats(incoming, onScreen - decodeMs, watching));
      }
      this.walkUntil = performance.now() + walkArrives;
      this.settleUntil = speakOver ? performance.now() + ms : this.walkUntil;
      window.setTimeout(() => {
        outgoing.destroy();
        this.busy = false;
        const q = this.queued;
        this.queued = null;
        if (q) this.asAuto(q.auto, () => this.goTo(q.id, q.transition, q.viaBack, q.origin));
        if (!this.busy) {
          const waiting = this.idle;
          this.idle = [];
          for (const w of waiting) w();
        }
      }, ms + 20);
    });
  }

  /**
   * A step is being left, by any path (`goTo`, a swipe): its walk and its rings end (a ring
   * left on the outgoing layer rode the transition out, and a scroll's copy of it, 2026-10-14), and a
   * press on the next step's control right after is the tail of this one (2026-10-14).
   */
  private leaveStep(): void {
    this.stopBeats();
    this.stopCues();
    this.stage.fadeCues();
    this.sayLeft = null;
    if (this.landLast === this.current.id) this.landLast = null;
    if (this.auto) return;
    if (this.tapGuard) window.clearTimeout(this.tapGuard);
    this.tapGuard = window.setTimeout(() => (this.tapGuard = 0), STEP_TAP_GUARD_MS);
  }

  /** Retrace one hop of `history` — the step the viewer actually arrived
   *  from, not `steps[index - 1]`. A no-op at the start of a session or
   *  right after a replay, when there is nowhere to go back to. */
  private goBack(): void {
    const prev = this.history.pop();
    if (prev) this.goTo(prev, 'slide-right', true);
  }

  /** The bar's arrows: one step back along the path the viewer took, or on
   *  along the step's own way forward (the end card after the last). Like any
   *  other input on the stage, it hands watch mode back to the viewer. */
  private stepBy(dir: -1 | 1): void {
    if (this.watch.playing) this.watch.stop('pause');
    if (dir < 0) return this.goBack();
    const next = this.nextIdFor(this.current);
    if (next) this.goTo(next);
    else this.finish();
  }

  /**
   * The lead form (`doc.gate`, ./gate.ts) is due when the viewer moves on from its step, or
   * past it (a chapter jump, a branch), or ends a demo before it: from a step at or before the
   * gate's to one after it, or to the end (`to` -1). Steps are compared by their order in the
   * document. Never twice in a page load, never going back.
   */
  private gateDue(to: number): boolean {
    const g = this.doc.gate;
    if (!g || this.gateClose || gateAnswered(this.doc.id)) return false;
    const at = this.doc.steps.findIndex((s) => s.id === g.afterStep);
    return at >= 0 && this.index <= at && (to < 0 || to > at);
  }

  /** Show the lead form; `then` (the move it held up) runs once it is answered or skipped. */
  private showGate(then: (() => void) | null): void {
    const g = this.doc.gate;
    if (!g) return;
    const auto = this.auto;
    const step = this.current.id;
    this.analytics.emit('gate_show', step);
    this.gateClose = openGate(this.viewport, g, this.doc.id, this.s, (values) => {
      this.gateClose = null;
      if (values) this.analytics.emit('gate_submit', step, values as Record<string, string>);
      else this.analytics.emit('gate_skip', step);
      try {
        this.root.focus({ preventScroll: true });
      } catch {
        this.root.focus();
      }
      // Watch held on the form goes on as Watch; a viewer's own move goes on as theirs.
      if (then) this.asAuto(auto && this.watch.playing, then);
      const waiting = this.gateWait;
      this.gateWait = [];
      for (const w of waiting) w();
    });
  }

  private finish(): void {
    if (this.ended || this.gateClose) return;
    if (this.gateDue(-1)) return this.showGate(() => this.finish());
    this.ended = true;
    // The end card carries the call to action; not floating over the pull-back to the whole screen.
    if (this.ctaTimer) window.clearTimeout(this.ctaTimer);
    this.ctaTimer = 0;
    this.chrome.hideCta();
    // The walk and its rings end here: they moved the camera under the end card (2026-10-14).
    this.stopBeats();
    this.stopCues();
    this.stage.fadeCues();
    // A story pulls back to the whole screen first, then the end card; not under a showcase's
    // panel, where the pull-back is not seen and was 2 s of nothing (2026-10-22).
    const hold = this.current.showcase ? 0 : this.zoomOut();
    const showEnd = (): void => {
      this.endTimer = 0;
      if (!this.ended) return;
      this.chrome.setEnded(true);
      this.caption?.hide();
      this.cards?.hide();
      // The end card carries the call to action now; one button, not two.
      this.chrome.hideCta();
    };
    if (hold > 0) this.endTimer = window.setTimeout(showEnd, hold);
    else showEnd();
    this.emit('complete', this.current.id);
    // For the host page: `/e/:slug` forwards this to its parent as a
    // `mado:complete` postMessage, which in-app help turns into `Mado.on('complete')`.
    this.root.dispatchEvent(new CustomEvent('mado:complete', { bubbles: true }));
  }

  /**
   * Phone story: pull the camera back to the whole screen and hold it, so the
   * viewer sees the full picture before the end card. Returns ms until the
   * end card may show (0: no story here, or a scroll step, which has no
   * single picture to pull back to).
   */
  private zoomOut(): number {
    const plan = this.storyPlan(this.current);
    if (!plan || this.current.scroll) return 0;
    this.stopBeats();
    const to = overviewLayout(plan.picture, this.stage.asset, this.overviewFrame());
    const from = this.stage.currentLayout;
    const ms = sameLayout(from, to) ? 0 : dur(walkMoveDuration(from, to, this.vp()));
    // The targets are not on the whole screen's framing: unseen, not tappable.
    this.overlay.root.classList.add('mado-overlay--wait');
    if (ms > 0) this.stage.setCamera(to, { ms, ease: EASE_INOUT });
    return ms + END_HOLD_MS;
  }

  private replay(): void {
    this.analytics.emit('replay');
    if (this.endTimer) window.clearTimeout(this.endTimer);
    this.endTimer = 0;
    this.stopBeats();
    this.ended = false;
    this.chrome.setEnded(false);
    // A fresh run: nothing to retrace back into from before the replay.
    this.history = [];
    this.goTo(this.doc.start, 'fade', true);
  }

  /* ── gestures ─────────────────────────────────────────────────────────── */

  private swipeHotspot(dir: SwipeDir): Hotspot | undefined {
    return this.current.hotspots.find((h) => h.gesture === 'swipe' && h.dir === dir);
  }

  /**
   * The target a vertical move completes, named by the way the finger moves:
   * finger up (wheel down) is a scroll, or an "up" swipe; finger down is a
   * "down" swipe.
   */
  private verticalHotspot(finger: 'up' | 'down'): Hotspot | undefined {
    return (
      this.swipeHotspot(finger) ??
      (finger === 'up' ? this.current.hotspots.find((h) => h.gesture === 'scroll') : undefined)
    );
  }

  /* ── scroll ───────────────────────────────────────────────────────────── */

  /** Scrollable distance in CSS px at the current layout. */
  private scrollRange(): number {
    return this.stage.scrollRange();
  }

  /**
   * Momentum after the finger leaves, decelerating like a real list.
   *
   * `v` is px/ms at release. Distance is velocity times the decay constant,
   * eased out so it arrives rather than stops.
   */
  private startFling(v: number): void {
    const range = this.scrollRange();
    const from = this.stage.scrollProgress;
    const delta = -(v * FLING_DECAY_MS) / range;
    const to = Math.max(0, Math.min(1, from + delta));
    this.wheelTo = null;
    if (to === from) return this.settleScroll();

    this.fling = tween(
      Math.min(FLING_MAX_MS, Math.abs(delta) * range * FLING_MS_PER_PX),
      (t) => {
        this.stage.setScrollProgress(from + (to - from) * t);
        this.overlay.reposition(this.current, this.stage.currentLayout, this.stage.asset);
      },
      () => {
        this.fling = null;
        this.settleScroll();
      },
    );
  }

  /** Advance once the reader has actually reached the end of the content. */
  private settleScroll(): void {
    if (this.stage.scrollProgress < 0.995) return;
    if (!this.auto) this.analytics.emit('scroll_complete', this.current.id);
    const next = this.current.next;
    if (next) this.goTo(next);
    else this.finish();
  }

  private onDrag(dx: number, dy: number, axis: 'x' | 'y'): void {
    if (this.busy || this.chrome.introOpen) return;

    if (this.current.scroll && this.waiting) this.skipBeats();
    if (
      axis === 'x' &&
      this.current.scroll &&
      this.stage.pans &&
      !this.swipeHotspot(dx < 0 ? 'left' : 'right')
    ) {
      // Phone story: the page's other columns, under the finger.
      this.panDrag ??= this.stage.pan;
      this.stage.setPan(this.panDrag + dx);
      this.overlay.reposition(this.current, this.stage.currentLayout, this.stage.asset);
      return;
    }

    if (axis === 'y' && this.current.scroll) {
      // `dy` is cumulative from the drag origin, so progress must be measured
      // from where the drag started. Applying it to the live value compounds
      // every frame and the content runs away from the finger.
      if (this.drag === null) {
        this.fling?.cancel();
        this.fling = null;
        this.drag = { from: this.stage.scrollProgress, dy: 0, at: performance.now(), v: 0 };
      }
      const now = performance.now();
      const dt = now - this.drag.at;
      if (dt > 0) this.drag.v = (dy - this.drag.dy) / dt;
      this.drag.dy = dy;
      this.drag.at = now;

      this.stage.setScrollProgress(this.drag.from - dy / this.scrollRange());
      this.overlay.reposition(this.current, this.stage.currentLayout, this.stage.asset);
      return;
    }

    if (axis !== 'x') return;
    const dir: SwipeDir = dx < 0 ? 'left' : 'right';
    const h = this.swipeHotspot(dir);
    if (!h) return;

    // Mount the target once, then let it travel with the finger. Reduced motion
    // skips the live tracking entirely — the commit still works.
    if (prefersReducedMotion()) return;
    if (!this.pending || this.pending.dir !== dir) {
      this.pending?.stage.destroy();
      const targetId = h.next ?? this.current.next;
      if (!targetId) return;
      const target = this.doc.steps.find((s) => s.id === targetId);
      if (!target) return;
      const stage = this.newStage(target, this.current);
      stage.layer.style.transition = 'none';
      // Under the finger the next screen comes in whole; the camera settles
      // on its target once the swipe commits (onDragEnd).
      if (this.cinematic) stage.setCamera(stage.restLayout());
      this.win.appendChild(stage.layer);
      this.pending = { stage, dir, hotspot: h };
    }

    const w = this.vp().w;
    this.stage.layer.style.transition = 'none';
    this.stage.layer.style.transform = `translate3d(${dx}px,0,0)`;
    this.pending.stage.layer.style.transform = `translate3d(${dx + (dir === 'left' ? w : -w)}px,0,0)`;
  }

  private onDragEnd(committed: SwipeDir | null): void {
    if (this.chrome.introOpen) return;
    if (this.panDrag !== null) {
      this.panDrag = null;
      return;
    }
    if (!this.current.scroll && (committed === 'up' || committed === 'down')) {
      const h = this.verticalHotspot(committed);
      if (h && !this.busy) this.activate(h);
      return;
    }
    if (this.current.scroll) {
      const v = this.drag?.v ?? 0;
      this.drag = null;
      // A list that stops dead under your finger is the clearest tell that a
      // demo is not the real app. Carry the throw, then settle.
      const capped = Math.max(-FLING_MAX_V, Math.min(FLING_MAX_V, v));
      if (Math.abs(capped) > FLING_MIN_V && !prefersReducedMotion()) this.startFling(capped);
      else this.settleScroll();
      return;
    }

    const p = this.pending;
    if (!p) {
      this.stage.layer.style.transition = `transform 220ms ${EASE}`;
      this.stage.layer.style.transform = 'translate3d(0,0,0)';
      return;
    }

    const w = this.vp().w;
    const ms = dur(240);
    const t = `transform ${ms}ms ${EASE}`;

    if (committed === p.dir) {
      if (!this.auto) this.analytics.emit('swipe', this.current.id, { dir: p.dir });
      const follow: Follow = {};
      // Where the finger left the incoming screen: the overlay starts there.
      const from = p.stage.layer.style.transform;
      if (ms > 0 && from && from !== REST) {
        follow.layer = { from, to: REST, origin: '50% 50%', transition: t };
      }

      const outgoing = this.stage;
      this.history.push(outgoing.step.id);
      // The same bookkeeping as `goTo`: one `step_exit` per change, the walk ended (2026-10-14).
      this.emit('step_exit', outgoing.step.id, { i: this.index });
      this.leaveStep();
      this.stage = p.stage;
      this.index = this.doc.steps.findIndex((s) => s.id === p.stage.step.id);
      this.pending = null;
      this.ended = false;
      this.chrome.setEnded(false);
      this.warmAhead(this.current);
      let settleMs = 0;
      let camMove: CameraMove | null = null;
      const target = p.stage.targetLayout();
      if (this.cinematic && !sameLayout(p.stage.currentLayout, target)) {
        const delay = Math.round(ms * 0.5);
        camMove = { ms: CAMERA_SETTLE_MS, delay, ease: EASE_INOUT };
        settleMs = dur(delay + CAMERA_SETTLE_MS);
        follow.cam = {
          from: p.stage.currentLayout,
          to: target,
          transition: cameraTransition(camMove),
        };
      }
      const arrives = Math.max(ms, settleMs);
      this.settleUntil = performance.now() + arrives;
      if (camMove) p.stage.aim(target);
      this.paint(this.cinematic ? arrives : 0);
      const moveOverlay = this.overlay.follow(follow);

      p.stage.layer.style.transition = t;
      outgoing.layer.style.transition = t;
      outgoing.layer.style.transform = `translate3d(${p.dir === 'left' ? -w : w}px,0,0)`;
      p.stage.layer.style.transform = REST;
      if (camMove) p.stage.setCamera(target, camMove);
      moveOverlay();
      this.emit('step_enter', this.current.id, { i: this.index });
      const ch = this.seekCut ? null : this.chapterAt(this.current.id);
      if (ch) this.chrome.flashChapter(ch.chapter.title, ch.index);
      window.setTimeout(() => outgoing.destroy(), ms + 20);
    } else {
      // Spring back. The pre-mounted target is discarded, not reused, so a
      // second attempt starts from a clean state.
      this.stage.layer.style.transition = t;
      this.stage.layer.style.transform = 'translate3d(0,0,0)';
      p.stage.layer.style.transition = t;
      p.stage.layer.style.transform = `translate3d(${p.dir === 'left' ? w : -w}px,0,0)`;
      const dead = p.stage;
      this.pending = null;
      window.setTimeout(() => dead.destroy(), ms + 20);
    }
  }

  private onWheel(deltaY: number): void {
    if (this.busy || this.chrome.introOpen) return;
    if (!this.current.scroll) {
      // A wheel is a stream of small deltas; a deliberate scroll adds up to
      // one step, a stray nudge does not. One activation per step.
      this.wheelSum += deltaY;
      if (Math.abs(this.wheelSum) < WHEEL_STEP_PX) return;
      const h = this.verticalHotspot(this.wheelSum > 0 ? 'up' : 'down');
      this.wheelSum = 0;
      if (h) this.activate(h);
      return;
    }
    if (this.waiting) this.skipBeats();
    // A wheel notch is ~100px in one event; applied at once the page lurches.
    // Glide to where the notches add up to, and only then look at the end.
    const from = this.stage.scrollProgress;
    const base = this.fling && this.wheelTo !== null ? this.wheelTo : from;
    const to = Math.max(0, Math.min(1, base + deltaY / this.scrollRange()));
    this.fling?.cancel();
    this.wheelTo = to;
    this.fling = tween(
      dur(WHEEL_GLIDE_MS),
      (t) => {
        this.stage.setScrollProgress(from + (to - from) * t);
        this.overlay.reposition(this.current, this.stage.currentLayout, this.stage.asset);
      },
      () => {
        this.fling = null;
        this.wheelTo = null;
        this.settleScroll();
      },
    );
  }

  /* ── keyboard & resize ────────────────────────────────────────────────── */

  private bindKeys(): void {
    this.root.addEventListener('keydown', (e) => {
      // The intro cover owns input until the viewer presses Start.
      if (this.chrome.introOpen) return;
      // A key that only paused watch mode (bindTakeover) navigates nowhere.
      if (e.defaultPrevented) return;
      // The volume slider's arrows move the slider, not the demo.
      if (e.target instanceof HTMLInputElement) return;
      const step = this.current;
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
        case 'Enter': {
          // Space and Enter belong to a focused button or link (that is how it
          // is pressed). ArrowRight never does — after a click on the bar's
          // own Next arrow, focus stays on that button and the key must still go on.
          if (
            e.key !== 'ArrowRight' &&
            (e.target instanceof HTMLButtonElement || e.target instanceof HTMLAnchorElement)
          )
            return;
          e.preventDefault();
          // Callouts open: the key goes on to the next one (the last opens the instruction).
          if (this.co && this.co.i >= 0) {
            this.calloutPress('next', 'key');
            break;
          }
          // On the instruction the key is the step's action: the control pressed, its one
          // `hotspot_click` (founder, 2026-10-15: back or forth by keys).
          if (this.overlay.pressTarget(step)) break;
          const next = this.nextIdFor(step);
          if (next) this.goTo(next);
          else this.finish();
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          // Back one card; from a step's first, the step before, on its last card.
          this.cardBack('key');
          break;
        }
        case 'Home':
          e.preventDefault();
          this.goTo(this.doc.start, 'fade');
          break;
        default:
          break;
      }
    });
  }

  /**
   * Re-lay out for the box the player has now, whoever tells of it. The ResizeObserver is one
   * way; the window's own `resize` (an iframe's box settling, as the claude.ai viewer's does) is
   * a second; and `fresh` checks again before a walk or a callout starts. The size is never
   * taken from the last report: the stage says what it was laid out for (`Stage.laidOutFor`),
   * so a report lost or coalesced on the way is put right at the next of any of them (the
   * founder's iPhone, 2026-10-16: a phone-edition step drawn at a short box's whole-width
   * framing, the picture a third of the screen, never re-laid out when the box grew).
   */
  /**
   * A phone or tablet frame: the bar goes under the device, on a plate of its own, and the device
   * takes the room above it, so the app's own bottom buttons are never under the controls. A box
   * taller than the device needs (a tablet on a phone) puts the device at the top and the step's
   * card under the bar, off the screen (`placeDock`).
   */
  private dock(): void {
    this.root.classList.add('tf--dock');
    this.dockEl = el('div', 'mado-dock');
    this.root.append(this.chrome.root, this.dockEl);
    this.chrome.setDocked(true);
    if (typeof ResizeObserver !== 'undefined') {
      this.dockRo = new ResizeObserver(() => this.placeDock());
      this.dockRo.observe(this.chrome.root);
      this.dockRo.observe(this.root);
    }
    this.placeDock();
  }

  /** Size the frame round the bar, put the bar under the device and the card place under it. */
  private placeDock(): void {
    const bar = this.chrome.root;
    const device = this.viewport.parentElement!;
    const frame = device.parentElement!;
    const W = this.root.clientWidth;
    const H = this.root.clientHeight;
    if (!(W > 0 && H > 0)) return;
    const barH = bar.offsetHeight;
    this.root.style.setProperty('--mado-dock-h', `${barH}px`);
    const cs = getComputedStyle(frame);
    const padX = parseFloat(cs.paddingLeft) || 0;
    const padY = parseFloat(cs.paddingTop) || 0;
    const { captureW, captureH } = this.doc.source;
    const availH = H - barH - DOCK_GAP_PX * 2 - padY;
    const devH = Math.min(availH, ((W - padX * 2) * captureH) / Math.max(1, captureW));
    const room = availH - devH >= DOCK_CARD_MIN_PX;
    if (room !== this.dockRoom) {
      this.dockRoom = room;
      this.root.classList.toggle('tf--dock-card', room);
      // The step's card moves between the screen and the place under the bar.
      if (this.stage) this.paint();
    }
    const r = this.root.getBoundingClientRect();
    const d = device.getBoundingClientRect();
    // As wide as the device, but never narrower than its controls need on one row.
    const barW = Math.min(W - DOCK_GAP_PX * 2, Math.max(d.width, DOCK_BAR_MIN_W));
    const x = Math.max(DOCK_GAP_PX, Math.round((W - barW) / 2));
    const top = Math.round(d.bottom - r.top + DOCK_GAP_PX);
    bar.style.left = `${x}px`;
    bar.style.right = `${x}px`;
    bar.style.top = `${top}px`;
    bar.style.bottom = 'auto';
    if (this.dockEl) {
      this.dockEl.style.left = `${x}px`;
      this.dockEl.style.right = `${x}px`;
      this.dockEl.style.top = `${top + barH + DOCK_GAP_PX * 1.5}px`;
    }
  }

  private observeResize(host: HTMLElement): void {
    let frame = 0;
    const check = (): void => {
      if (typeof requestAnimationFrame === 'undefined') return void this.fresh();
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.fresh());
    };
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(check);
      this.ro.observe(host);
      // A docked bar that grows (Watch's timeline row) takes its room from the screen.
      if (this.docked) this.ro.observe(this.viewport);
    }
    if (typeof window !== 'undefined') {
      this.onWinResize = check;
      window.addEventListener('resize', check);
      window.visualViewport?.addEventListener('resize', check);
    }
  }

  /**
   * The stage was laid out for another box than the player has now: a size more than
   * `RESIZE_MIN_PX` off (sub-pixel rounding, a host nudging its box by a pixel, is nothing to
   * redo, and redoing it cut a walk short, 2026-10-14), or a story told where none is now (or
   * the reverse). No size yet (a hidden box) keeps the last layout until there is one.
   */
  private stale(): boolean {
    const vp = this.vp();
    if (vp.w <= 0 || vp.h <= 0) return false;
    const at = this.stage.laidOutFor;
    if (Math.abs(vp.w - at.w) >= RESIZE_MIN_PX || Math.abs(vp.h - at.h) >= RESIZE_MIN_PX) {
      return true;
    }
    return !!this.storyPlan(this.current) !== this.stage.framed;
  }

  /** Lay everything out again when the stage is stale (`stale`); true when it was. */
  private fresh(calloutAt?: number): boolean {
    if (!this.stale()) return false;
    this.resized(calloutAt);
    return true;
  }

  /**
   * Everything that depends on the box, again, for the current step and state: the camera and
   * picture (`Stage.relayout`), the story's window, the targets, a walk under way, the callout
   * on show (or `calloutAt`), the presenter.
   */
  private resized(calloutAt?: number): void {
    const vp = this.vp();
    // Callouts open: the same one again at the new size, its framing planned for it.
    const co = this.co?.stage === this.stage ? this.co : null;
    const callout = co ? (calloutAt ?? Math.max(0, co.i)) : -1;
    const calloutAuto = !!co?.auto;
    if (callout >= 0) {
      this.stopCues();
      this.stage.clearCues();
    }
    // A walk under way goes on at the new size from where it had got to, rings included
    // (it used to jump to its end, the rings dropped, 2026-10-14).
    const clock = this.waiting && this.walkClock?.stage === this.stage ? this.walkClock : null;
    if (clock) {
      this.stopBeats();
      this.stopCues();
      this.stage.clearCues();
    } else if (this.waiting) this.skipBeats();
    const plan = this.storyPlan(this.current);
    // The step's phone track and the demo's zoom are planned for the window: planned again for
    // this one (a box that settles in steps, a phone turned, a story told where none was).
    this.stage.relayout(vp, plan?.picture ?? null, this.sizesFor(this.current), {
      step: this.cardsStep(this.current),
      zoom: plan ? this.walkZoom() : undefined,
    });
    if (callout >= 0) this.openCallouts(this.stage, 0, callout, calloutAuto);
    if (clock) {
      const elapsed = performance.now() - clock.origin;
      const arrives = this.playBeats(clock.stage, clock.startMs - elapsed, clock.watching);
      this.walkUntil = performance.now() + Math.max(0, arrives);
      if (!this.waiting) {
        this.overlay.render(this.current, this.stage.currentLayout, this.stage.asset, {
          settleMs: 0,
        });
      }
    }
    this.showCaption(this.current);
    this.overlay.reposition(this.current, this.stage.currentLayout, this.stage.asset);
    if (this.presenter.active) {
      this.presenter.layout(vp, this.presenterAvoid(this.current, this.stage.currentLayout));
    }
  }

  /* ── phone story ─────────────────────────────────────────────────────── */

  private overview: NormRect | null = null;

  /** What a whole-screen view shows: the page's content box (`overviewFrame`), once per demo. */
  private overviewFrame(): NormRect {
    return (this.overview ??= overviewFrame(this.doc));
  }

  /** A step's stage, framed for the story when one is told (story.ts). */
  private newStage(step: Step, from?: Step): Stage {
    const plan = this.storyPlan(step);
    return new Stage(
      this.doc,
      this.cardsStep(step),
      this.vp(),
      this.allowFocus,
      this.assetBase,
      plan?.picture ?? null,
      // A scroll straight on from a scroll carries on down the page; from
      // anywhere else it opens on the whole page first.
      !from?.scroll,
      this.sizesFor(step),
      plan ? this.walkZoom() : undefined,
      // A new screen opens whole; the same screen carries on (phone-overview.md §2.2).
      !!from && !this.sameScreenAs(from, step),
      // A desktop step on the screen the viewer is looking at starts where the camera is.
      !plan && this.stage && !this.stage.framed && this.stage.step === from
        ? desktopEntry(from, this.stage.liveLayout(), step, this.doc.assets, this.doc.theme.motion)
        : null,
    );
  }

  /** Is `to` on the screen the viewer is looking at, coming from `from` (`sameScreen`)? */
  private sameScreenAs(from: Step | null | undefined, to: Step): boolean {
    return sameScreen(from, to, this.doc.assets);
  }

  /** The demo's one close-up zoom on this viewport (`walkZoom`); undefined when no story is told. */
  private walkZoom(): number | undefined {
    const vp = this.vp();
    if (!this.caption || !storyOn(this.doc, vp)) return undefined;
    const key = `${vp.w}x${vp.h}`;
    if (key !== this.zoomKey) {
      this.zoomKey = key;
      this.zoomVal = walkZoom(this.doc, walkFrame(vp));
    }
    return this.zoomVal;
  }

  /**
   * The width a step's picture is asked for — the widest it is ever drawn
   * (sizing.ts) — shared by the stage and prefetch so both want one file.
   * Before the player has a size (not yet laid out), a phone's.
   */
  private sizesFor(step: Step): number {
    const vp = this.vp();
    const viewport = vp.w > 0 && vp.h > 0 ? vp : { w: 390, h: 844 };
    const w = stepSizes(this.doc, step, {
      viewport,
      allowFocus: this.allowFocus,
      motion: this.doc.theme.motion,
      pictureFor: (s) => this.storyPlan(s)?.picture ?? null,
      frame: this.overviewFrame(),
      ...(this.walkZoom() ? { zoom: this.walkZoom()! } : {}),
    });
    // On a phone the close-ups ask for more than the largest file at DPR 3
    // (1850 CSS px x 3), so every step took the 2880 file: ~21 MB decoded
    // each, and iOS Safari reloads the tab when it runs out of memory
    // (docs/build-log.md, "Phone memory"). A phone takes at most the 1920 file.
    if (!this.storyPlan(step)) return w;
    const dpr = Math.min(3, Math.max(1, globalThis.devicePixelRatio || 1));
    return Math.min(w, Math.max(viewport.w, PHONE_MAX_FILE_W / dpr));
  }

  /**
   * Warm what can follow `step`: its pictures (Prefetcher) and, in a demo
   * with presenter clips, the same reachable steps' clips, so a branch is
   * warmed whichever way the viewer (or watch mode) goes.
   */
  private warmAhead(step: Step): void {
    this.prefetch.warm(step);
    if (this.presenter.active) {
      for (const s of reachable(this.doc, step)) this.presenter.prefetch(s);
    }
  }

  /** A phone story is told on this viewport (`storyOn`): not on a desktop, a tablet, a phone capture. */
  private told(): boolean {
    return !!this.cards && storyOn(this.doc, this.vp());
  }

  /** Where a step's picture and caption go, or null when no story is told for it. */
  private storyPlan(step: Step): CardsPlan | StoryPlan | null {
    if (!this.told()) return null;
    const vp = this.vp();
    if (!this.doc.assets[step.scroll ? step.scroll.assetTall : step.asset]) return null;
    // A showcase: the whole screen above the bar, Watch's scrub row included; no headline.
    if (step.showcase) {
      return {
        picture: walkFrame(vp, CARDS_CHROME_PX),
        caption: { x: 0, y: 0, w: vp.w, h: 0 },
        radius: 0,
      };
    }
    const m = this.cardsMetrics(vp);
    return planCards(vp, m.shape, m.headH, m.cardsH.get(step.id) ?? 0, m.top);
  }

  /**
   * A step's sentence cards (cards.ts `cardLines`): none when it says no more than its headline,
   * and none where no story is told (a desktop's words are callouts beside the rings).
   */
  private cardLinesOf(step: Step): string[] {
    if (!this.told()) return [];
    return cardLines(substitute(step.narration?.text ?? '', this.vars), captionText(step));
  }

  /**
   * What the demo's window and words need on this viewport: the screens' shape, the tallest
   * headline (one headline block for every step), each step's sentences, and where the block
   * starts (the tallest step's, centred). Also each step with its close-ups planned for its own
   * window (`cardsStep`).
   */
  private cardsMetrics(vp: { w: number; h: number }): NonNullable<typeof this.cardsFor> {
    const key = `${vp.w}x${vp.h}`;
    if (this.cardsFor?.key === key) return this.cardsFor;
    const steps = resolveLinearPath(this.doc).filter((s) => !s.showcase);
    const shapes = steps
      .filter((s) => !s.scroll)
      .map((s) => this.doc.assets[s.asset])
      .filter((a): a is NonNullable<typeof a> => !!a)
      .map((a) => a.w / Math.max(1, a.h))
      .sort((a, b) => a - b);
    const shape = shapes.length ? shapes[Math.floor(shapes.length / 2)]! : 1.6;
    const w = vp.w - CARDS_MARGIN_PX * 2;
    let headH = 0;
    let tallest = 0;
    const cardsH = new Map<string, number>();
    for (const s of steps) {
      headH = Math.max(headH, this.caption!.heightFor(s, vp.w));
      const h = this.cards!.heightFor(this.cardLinesOf(s), w);
      cardsH.set(s.id, h);
      tallest = Math.max(tallest, h);
    }
    const most = planCards(vp, shape, headH, tallest);
    const top = most.caption.y;
    // Each window size its own phone track plan: a close-up fills the window it is shown in.
    const bySize = new Map<string, Map<string, Step>>();
    const planned = new Map<string, Step>();
    for (const s of steps) {
      const { picture: p } = planCards(vp, shape, headH, cardsH.get(s.id) ?? 0, top);
      const size = `${p.w}x${p.h}`;
      let all = bySize.get(size);
      if (!all) {
        all = new Map(planPhoneTracks(this.doc, { w: p.w, h: p.h }).steps.map((x) => [x.id, x]));
        bySize.set(size, all);
      }
      planned.set(s.id, all.get(s.id) ?? s);
    }
    this.cardsFor = { key, shape, headH, cardsH, top, steps: planned };
    return this.cardsFor;
  }

  /** The step with its phone track planned for the cards' window, when a story is told. */
  private cardsStep(step: Step): Step {
    if (step.showcase || !this.storyPlan(step)) return step;
    return this.cardsMetrics(this.vp()).steps.get(step.id) ?? step;
  }

  private showCaption(step: Step): void {
    if (!this.caption || !this.cards) return;
    const plan = this.storyPlan(step);
    this.overlay.walk = !!plan;
    // The headline above the window says what to do, so no bubble says it.
    this.overlay.headline = !!plan && 'cards' in plan && !!captionText(step);
    this.viewport.classList.toggle('mado-viewport--story', !!plan);
    if (plan && 'cards' in plan) {
      // The step's headline above the window, its sentences under it (a showcase has neither).
      this.caption.show(step, plan);
      this.cards.show(this.cardLinesOf(step), plan.cards, -1, null);
    } else {
      this.caption.hide();
      this.cards.hide();
    }
    this.frameWindow(plan, step);
  }

  /**
   * Clip the step layers to the story's window (rounded), or lift the clip
   * when no story is told. `clip-path` keeps the layers' own coordinates
   * (they are laid out in viewport px), so a slide or a walk moves inside the
   * frame and nothing else has to know about it.
   */
  private frameWindow(plan: StoryPlan | null, step: Step): void {
    const w = this.win.style;
    if (!plan) {
      // A fill still on its way (`edgeFill` below) must not land on a window with no story.
      this.fillStep = '';
      w.clipPath = '';
      w.background = '';
      this.overlay.setBounds(null);
      return;
    }
    // Behind a picture that does not fill the window: more of the page, not black.
    const asset = this.doc.assets[step.scroll ? step.scroll.assetTall : step.asset];
    const fillFor = step.id;
    this.fillStep = fillFor;
    void edgeFill(asset?.placeholder).then((fill) => {
      if (this.fillStep === fillFor && fill) this.win.style.background = fill;
    });
    const { picture: p, radius } = plan;
    const vp = this.vp();
    w.clipPath = `inset(${p.y}px ${vp.w - p.x - p.w}px ${vp.h - p.y - p.h}px ${p.x}px round ${radius}px)`;
    // The targets, the typed text and the spotlight belong to the picture:
    // cut to the same window, so none of them spills into the margin.
    this.overlay.setBounds(p, radius);
  }

  /** Phone walkthrough: a free drag looks around the step's picture, at its zoom. */
  private onLook(dx: number, dy: number): void {
    if (this.busy || this.chrome.introOpen || this.current.scroll) return;
    const plan = this.storyPlan(this.current);
    if (!plan) return;
    if (this.waiting) this.skipBeats();
    this.lookFrom ??= this.stage.currentLayout;
    const layout = lookLayout(
      { ...this.lookFrom, tx: this.lookFrom.tx + dx, ty: this.lookFrom.ty + dy },
      plan.picture,
      this.stage.asset,
    );
    this.stage.setCamera(layout);
    this.overlay.reposition(this.current, layout, this.stage.asset);
  }

  private onLookEnd(): void {
    if (this.lookFrom === null) return;
    this.lookFrom = null;
    this.swallowClick = true;
    // A click, if one comes, follows pointerup within a moment.
    window.setTimeout(() => (this.swallowClick = false), LOOK_CLICK_MS);
  }

  /**
   * Phone walkthrough: pinch in for the whole screen, spread back to the
   * step's own framing. The targets stay live and follow the picture.
   */
  private onPinch(ratio: number): void {
    if (this.busy || this.chrome.introOpen || this.current.scroll) return;
    const plan = this.storyPlan(this.current);
    if (!plan) return;
    if (this.waiting) this.skipBeats();
    // Spread: the step's close-up — its target, even on an overview step,
    // which rests on the whole screen (docs/plans/phone-overview.md §2.1).
    const to =
      ratio < 1
        ? overviewLayout(plan.picture, this.stage.asset, this.overviewFrame())
        : this.stage.closeLayout();
    const from = this.stage.currentLayout;
    if (sameLayout(from, to)) return;
    const ms = dur(walkMoveDuration(from, to, this.vp()));
    this.stage.setCamera(to, { ms, ease: EASE_INOUT });
    this.overlay.render(this.current, to, this.stage.asset, { settleMs: ms });
  }

  /**
   * Walk the stage through its shots after `startMs`: rest on each, then
   * move to the next (a cut under reduced motion). The targets stay unseen
   * and untappable until the last shot — they are not on screen before it —
   * and a tap on the picture meanwhile skips to it. Returns ms until arrival.
   *
   * `watching`: Watch mode, where the holds follow the step's narration — on
   * a phone the opening shot is held over its lead-in (`watchEstablishHold`);
   * on a desktop's guided step every lead-in stretches so the control comes
   * as the narration reaches it (`guideHolds`).
   */
  private playBeats(stage: Stage, startMs: number, watching = false): number {
    // `startMs` below 0: the walk is re-run from part-way through (a resize, `observeResize`).
    this.walkClock = { stage, origin: performance.now(), startMs, watching };
    if (stage.step.showcase) return this.playShowcase(stage, startMs, watching);
    this.endShowcase();
    const vp = this.vp();
    const walk = this.walkZoom() !== undefined;
    const narration = watching ? this.audio.narrationMs(stage.step) : null;
    if (stage.cued) return this.playCues(stage, startMs, narration, walk, watching);
    const beats = stage.beats;
    if (!beats || beats.length < 2) return startMs;
    const moves: number[] = [];
    for (let i = 1; i < beats.length; i++) {
      const a = beats[i - 1]!.layout;
      const b = beats[i]!.layout;
      moves.push(dur(walk ? walkMoveDuration(a, b, vp) : panDuration(a, b, vp)));
    }
    const holds = stage.framed
      ? beats.map((b, i) => (i === 0 && watching ? watchEstablishHold(narration) : b.holdMs))
      : guideHolds(beats, moves, narration, stage.step.view);
    let t = startMs;
    for (let i = 1; i < beats.length; i++) {
      t += holds[i - 1]!;
      // A move already over (a re-run part-way through) is a cut to where it went; one under
      // way runs for the time it has left.
      const pan = t < 0 ? Math.max(0, t + moves[i - 1]!) : moves[i - 1]!;
      const layout = beats[i]!.layout;
      this.beatTimers.push(
        window.setTimeout(
          () => stage.setCamera(layout, pan > 0 ? { ms: pan, ease: EASE_INOUT } : null),
          Math.max(0, t),
        ),
      );
      t += moves[i - 1]!;
    }
    this.waiting = true;
    this.overlay.root.classList.add('mado-overlay--wait');
    this.beatTimers.push(window.setTimeout(() => this.stopBeats(), t));
    return t;
  }

  /**
   * A showcase step (`Step.showcase`): its area lifted out, the headline, each line with its
   * labels, on the step's clock from `startMs` (below 0: part-way through, a seek or a resize).
   * Its targets and tooltip stay away meanwhile; a tap shows it whole (`skipBeats`). Returns ms
   * until it is over.
   */
  private playShowcase(stage: Stage, startMs: number, watching: boolean): number {
    this.stopCues();
    this.sc?.destroy();
    this.sc = null;
    const step = stage.step as ShowcaseStep;
    const narration = watching ? this.audio.narrationMs(step) : null;
    const end = startMs + showcasePlan(step, narration, this.showcasePhone(step)).ms;
    this.waiting = true;
    this.beatTimers.push(window.setTimeout(() => this.stopBeats(), Math.max(0, end)));
    this.overlay.root.classList.add('mado-overlay--sc');
    const M = globalThis.MadoWatch;
    if (!M) {
      // Its drawing comes with watch.js (fetched at once for a demo with showcases): drawn from
      // where the step's clock has got to when it arrives.
      const asked = performance.now();
      void loadWatch().then(
        () => {
          if (this.stage === stage && this.current.showcase && this.waiting) {
            this.drawShowcase(
              stage,
              step,
              narration,
              startMs - (performance.now() - asked),
              watching,
            );
          }
        },
        () => {},
      );
      return end;
    }
    this.drawShowcase(stage, step, narration, startMs, watching);
    return end;
  }

  /** The showcase is laid out on a phone (the view's own test, on the box it is given). */
  private showcasePhone(step: Step): boolean {
    const vp = this.vp();
    const box = this.storyPlan(step)?.picture ?? vp;
    return box.w / Math.max(1, box.h) < 0.9;
  }

  private drawShowcase(
    stage: Stage,
    step: ShowcaseStep,
    narration: number | null,
    startMs: number,
    watching: boolean,
  ): void {
    const M = globalThis.MadoWatch;
    if (!M) return;
    // A relayout asked for the drawing again while watch.js was on its way: one view, never two.
    this.sc?.destroy();
    const logo = this.doc.theme.logo ? this.doc.assets[this.doc.theme.logo] : undefined;
    const view = new M.ShowcaseView(
      stage.layer,
      step,
      stage.asset,
      this.assetBase,
      narration,
      () => stage.liveLayout(),
      {
        eyebrow: this.chapterTitle(step.id) || this.doc.title,
        logo: logo ? bestUrl(logo, 112, devicePixelRatio || 1, this.assetBase) : null,
        // Click-through: once it has played, Next says how to go on (founder, 2026-10-21).
        onNext: watching ? undefined : () => this.showcaseNext(),
        nextLabel: this.s.next,
        corner: productRadiusPx(this.doc.theme, stage.asset),
        ink: inkRects(stage.asset.ink),
        phone: this.showcasePhone(step),
      },
    );
    this.sc = view;
    const vp = this.vp();
    view.layout(this.storyPlan(step)?.picture ?? { x: 0, y: 0, w: vp.w, h: vp.h });
    if (startMs > 0) {
      this.beatTimers.push(window.setTimeout(() => view.play(0), startMs));
    } else view.play(-startMs);
  }

  /** A showcase's Next: on as a tap on a step with nothing to tap goes on. */
  private showcaseNext(): void {
    if (!this.current.showcase || this.busy) return;
    const next = this.nextIdFor(this.current);
    if (next) this.goTo(next);
    else this.finish();
  }

  /** Off a showcase step: its view goes with the stage, its hold on the overlay with it. */
  private endShowcase(): void {
    this.sc?.stop();
    this.sc = null;
    this.overlay.root.classList.remove('mado-overlay--sc');
  }

  /**
   * Narration cues (docs/plans/desktop-guided-camera.md §cues): the camera
   * moves and each cue's mark shows on the narration's clock (`cuePlan`),
   * from `startMs` — when the voice starts. Click-through and silent Watch
   * run at reading pace. Under a voice the targets stay unseen until the camera
   * has come to the control (an overview: until the line is over). With no
   * voice (`narration` null: click-through, Watch muted) nothing is said that
   * the target would talk over: it and its tooltip are drawn and live from the
   * start, riding the camera's moves, and the marks play beside them
   * (desktop-polish F8). Returns ms until the camera, and the line, are done.
   */
  private playCues(
    stage: Stage,
    startMs: number,
    narration: number | null,
    walk: boolean,
    watching = false,
  ): number {
    const vp = this.vp();
    const plan = stage.cuePlanFor(narration);
    const dim = spotColor(
      this.doc.theme.background,
      stage.framed ? undefined : DESKTOP_CUE_DIM_ALPHA,
    );
    const move = (a: Layout, b: Layout): number =>
      dur(walk ? walkMoveDuration(a, b, vp) : panDuration(a, b, vp));
    // No voice: the targets are live from the start. A step that types is too; its typing waits
    // for the walk's last move (`trackOverlay`), so no redraw restarts it.
    const live = narration === null;
    // Where the words are not heard and not in a caption band, each ring carries its words
    // (founder, 2026-10-15: rings "blink shortly with frames on a few blocks without any
    // explanation"). Click-through: the viewer drives them, one callout at a time with Next
    // (founder, 2026-10-15: the timed labels went "too fast, I didn't manage to read anything").
    // Muted Watch plays itself: a label beside each ring at the line's reading pace.
    const labels = live && !!stage.step.narration?.text.trim();
    // A phone story lights by the dim alone (no ring, no label beside it); its words are cards.
    const cards = this.told();
    this.sayLeft = null;
    if (cards && !live) this.lightSentences(stage, startMs, narration!);
    if (labels && this.hasCallouts(stage)) {
      return this.openCallouts(stage, startMs, undefined, watching);
    }
    // Click-through with no words to show (a caption band, no line): the marks are a quick tour
    // of what the line names, not the line at reading pace with the viewer waiting on it
    // (founder, 2026-10-14). Muted Watch plays at a silent reader's pace (`SILENT_READ_FACTOR`).
    const pace = live ? (watching ? SILENT_READ_FACTOR : tourPace(plan)) : 1;
    /** Plan time (the line's reading pace) to this walk's clock. */
    const T = (ms: number): number => ms * pace;
    // With no voice the viewer is the one to click: the camera's own lean into a small mark does
    // not take the target out of view (a lean into a side panel hid the toolbar's Actions for
    // 5.6 s, desktop-check F8). A zoom the line asks for still plays.
    const targets = stage.step.hotspots.map((h) => h.geom);
    const screen = { x: 0, y: 0, w: vp.w, h: vp.h };
    const beats =
      live && targets.length
        ? plan.beats.filter(
            (b) =>
              b.reason !== 'lean' ||
              targets.every((g) => rectOnScreen(g, b.layout, screen, stage.asset)),
          )
        : plan.beats;
    if (live) {
      const still = beats.length < 2;
      this.beatTimers.push(
        window.setTimeout(() => this.trackOverlay(stage, stage.liveLayout(), null, still), startMs),
      );
    }
    let arrive = startMs;
    for (let i = 1; i < beats.length; i++) {
      const b = beats[i]!;
      const full = b.ms !== undefined ? dur(b.ms) : move(beats[i - 1]!.layout, b.layout);
      const at = startMs + T(b.atMs);
      const last = i === beats.length - 1;
      // Re-run part-way through (a resize): a move already over is a cut, one under way runs
      // for the time it has left.
      const ms = at < 0 ? Math.max(0, at + full) : full;
      this.beatTimers.push(
        window.setTimeout(
          () => {
            const cam = { ms, ease: EASE_INOUT };
            const start = live ? this.trackOverlay(stage, b.layout, cam, last) : null;
            stage.setCamera(b.layout, cam);
            start?.();
            // A cursor resting on the control rides the move with it.
            this.watch.cameraPan(ms, EASE_INOUT);
          },
          Math.max(0, at),
        ),
      );
      arrive = at + full;
    }
    // The camera's walk ends here; a silent walk's targets are live before it.
    const camArrive = arrive;
    let firstRing = Infinity;
    let lastRing = 0;
    /** The rings as drawn, in order; a `look` at the target is the cursor (below). */
    const rings: {
      mark: Cue;
      on: number;
      off: number;
      under: boolean;
      words: string | null;
    }[] = [];
    for (const c of plan.cues) {
      // As drawn (`cueMark`): a `look` is the pointer only at the step's tap target, anything
      // else named is lit like a highlight, so the cursor only ever goes where it taps
      // (founder, 2026-10-12); on a phone a ring too big for the window is the part inside it.
      // A mark already over (a re-run part-way through) is not lit again.
      if (startMs + T(c.endMs) <= 0) continue;
      const mark = cueMark(c);
      if (mark.effect === 'look') {
        // The cursor points at the control, below its words, and stays: Watch then presses it
        // from there (`Watch.act`). Watch only: click-through has no cursor, and its pulse is
        // already on the control.
        const r = mark.rect;
        this.cueTimers.push(
          window.setTimeout(
            () => this.watch.point(r.x + r.w / 2, r.y + r.h),
            startMs + T(c.startMs),
          ),
        );
        continue;
      }
      // A ring reaching into the chrome bar's band would be drawn under the bar: the bar steps
      // aside while it is lit (mado Studio s8, the Sources/Devices/Countries cards at the bottom
      // of an overview, 2026-10-13). Measured where the camera is, or is going, at the cue.
      let at = beats[0]?.layout ?? stage.currentLayout;
      for (const b of beats) if (b.atMs <= c.startMs) at = b.layout;
      const ring = projectRect(mark.rect, at, stage.asset);
      const under = vp.h > 0 && ring.y + ring.h > vp.h - this.barBand();
      const on = T(c.startMs);
      const off = T(c.endMs);
      const words = labels ? substitute(c.cue.phrase, this.vars) : null;
      rings.push({ mark, on, off, under, words });
      firstRing = Math.min(firstRing, on);
      lastRing = Math.max(lastRing, off);
    }
    // Each ring is its own node with its own dim. A ring that follows the last within
    // `CUE_DIM_HOLD_MS` (the camera moving between the two) takes the dim over as the last one
    // goes: its hole is drawn at once and its ring comes at its phrase, so the page is not
    // brightened and greyed again a moment later (player-feel 2, 2026-10-15: "blinking").
    const nodes: (HTMLElement | null)[] = rings.map(() => null);
    rings.forEach((g, k) => {
      const next = rings[k + 1];
      const gap = next ? next.on - g.off : Infinity;
      const hands = !!next && gap > 0 && gap < CUE_DIM_HOLD_MS * pace;
      this.cueTimers.push(
        window.setTimeout(() => {
          const held = nodes[k];
          // Its words beside it while it is lit.
          const label = g.words ? { text: labelText(g.words), ...this.overlay.labelRoom() } : null;
          // A phone story lights by the dim alone: no ring, no label beside it.
          if (held) {
            if (!cards) stage.lightCue(held, label);
          } else nodes[k] = stage.showCue(g.mark, dim, !cards, label);
          if (g.under) this.chrome.setCueYield(true);
        }, startMs + g.on),
        window.setTimeout(() => {
          // The next one's dim replaces this one (`showCue` fades the one on show out).
          if (hands && next) nodes[k + 1] = stage.showCue(next.mark, dim, false);
          else if (nodes[k]) stage.hideCue(nodes[k]!);
          if (g.under) this.chrome.setCueYield(false);
        }, startMs + g.off),
      );
    });
    // While the rings play, the control's pulse steps aside (`ringLit`), first ring to last: a
    // pulse coming back in the short gaps between rings would flash. Click-through too: one thing
    // lit at a time (founder, 2026-10-15); the tour is short and the tooltip says what to click.
    if (lastRing > 0) {
      this.cueTimers.push(
        window.setTimeout(() => this.ringLit(true), startMs + firstRing),
        window.setTimeout(() => this.ringLit(false), startMs + lastRing),
      );
    }
    // One thing lit at a time: under a voice the control is drawn once the last ring is off, not
    // when the camera happens to settle (Talentika s1: Jobs pulsed while "the new ones waiting
    // for review" was still lit, founder 2026-10-14).
    if (!live && lastRing > 0) arrive = Math.max(arrive, startMs + lastRing);
    // An overview has no control to press until the line is over: its target is not drawn (it
    // would pulse over the narration the whole time), and a tap brings it (`skipBeats`).
    const text = this.current.narration?.text.trim();
    if (plan.targetMs === null && text) {
      arrive = Math.max(arrive, startMs + (narration ?? readingMs(text) * pace));
    }
    // Silent: nothing to wait for but the camera — a tap off the target still skips to it.
    const hold = live ? camArrive : arrive;
    if (hold > startMs) {
      this.waiting = true;
      this.liveWalk = live;
      if (!live) this.overlay.root.classList.add('mado-overlay--wait');
      this.beatTimers.push(window.setTimeout(() => this.stopBeats(), hold));
    }
    return arrive;
  }

  /**
   * Under a voice the sentence cards follow it: each lights when it starts being said (the same
   * clock as the camera), the rest faint. A step re-run from part-way through lights the one
   * the clip has reached.
   */
  private lightSentences(stage: Stage, startMs: number, narrationMs: number): void {
    const text = substitute(stage.step.narration?.text ?? '', this.vars);
    const lines = this.cardLinesOf(stage.step);
    lines.forEach((line, i) => {
      const at =
        i === 0 ? 0 : (phraseAtMs(text, line, narrationMs) ?? (narrationMs * i) / lines.length);
      this.cueTimers.push(
        window.setTimeout(() => this.cards?.light(i, null), Math.max(0, startMs + at)),
      );
    });
  }

  /* ── callouts (click-through) ─────────────────────────────────────────── */

  /**
   * A step's rings in click-through, led by the viewer (founder, 2026-10-15: "the viewer should
   * drive it; if he doesn't want to drive it he can Watch"). Each ring is lit with a card beside
   * it holding its sentence, and Next goes on; nothing moves by itself. The targets are drawn and
   * live throughout (a press on one is the step's action), their pulse aside while a ring is lit;
   * the instruction comes after the last callout. Each ring's framing is the desktop plan's for
   * it (the calm rules, a lean that hides the target left out), taken only when the ring is not
   * readable where the camera is. `at`: open on that callout (a resize, Back from the
   * instruction). Returns `startMs`.
   */
  private openCallouts(stage: Stage, startMs: number, at?: number, auto = false): number {
    const { list, rest } = this.calloutPlan(stage);
    const co = { stage, list, i: -1, rest, node: null, card: null, timer: 0, auto };
    this.co = co;
    this.overlay.callouts = true;
    this.holdLastCta();
    this.ringLit(true);
    // Back from the step after: open on the last card, the instruction (or the last callout
    // when the step goes straight on from it). A walk re-timed once the picture decodes opens
    // them again the same way; `at` (a resize, Back from the instruction) names the callout.
    // A seek into muted Watch: the callout the clock had reached.
    if (at === undefined && auto && startMs < 0) at = this.calloutAt(stage, list, -startMs);
    const last = at === undefined && this.landLast === stage.step.id;
    const k = last ? list.length - 1 : Math.min(at ?? 0, list.length - 1);
    const toInstruction = last && this.calloutsInstruction();
    // A phone story: the cards sit under the window, not on the picture, so the step opens
    // with its card lit and its Next on it; only the dim waits for the screen to arrive (founder,
    // 2026-10-22: the cards came unlit, then lit a second later).
    const early = this.told() && !auto && !toInstruction && startMs > 0;
    if (early) {
      co.i = k;
      // Planned while the step comes in, before it is the stage on show (`goTo`): drawn once it is.
      void Promise.resolve().then(() => {
        if (this.co === co && co.i === k) this.placeCard();
      });
    }
    co.timer = window.setTimeout(
      () => {
        // Next was pressed meanwhile: that card is on now.
        if (early && co.i !== k) return;
        if (toInstruction) this.closeCallouts();
        else this.showCallout(k);
      },
      Math.max(0, startMs),
    );
    return startMs;
  }

  /** A step's callouts: each ring with its framing and words, and the sentences left over. */
  private calloutPlan(stage: Stage): {
    list: (CalloutText & { mark: Cue | null; layout: Layout })[];
    rest: string;
  } {
    const plan = stage.cuePlanFor(null);
    const vp = this.vp();
    const screen = { x: 0, y: 0, w: vp.w, h: vp.h };
    const targets = stage.step.hotspots.map((h) => h.geom);
    const beats = plan.beats.filter(
      (b) =>
        b.reason !== 'lean' || targets.every((g) => rectOnScreen(g, b.layout, screen, stage.asset)),
    );
    const rings = plan.cues
      .filter((c) => cueMark(c).effect !== 'look')
      .sort((a, b) => a.startMs - b.startMs);
    const line = substitute(stage.step.narration?.text ?? '', this.vars);
    const { items, rest } = calloutTexts(
      line,
      rings.map((c) => substitute(c.cue.phrase, this.vars)),
    );
    const at = (c: (typeof rings)[number] | null): Layout => {
      let layout = beats[0]?.layout ?? stage.currentLayout;
      if (c) for (const b of beats) if (b.atMs <= c.startMs + 1) layout = b.layout;
      return layout;
    };
    // A phone story: every sentence is a card the viewer goes through, in order, lit with
    // the part it names when it names one; nothing is left over for a bubble to say again.
    const lines = this.cardLinesOf(stage.step);
    if (lines.length) {
      const list = lines.map((text) => {
        const k = items.findIndex((it) => it.text === text);
        const c = k >= 0 ? rings[k]! : null;
        return {
          text,
          span: k >= 0 ? items[k]!.span : null,
          mark: c ? cueMark(c) : null,
          layout: at(c),
        };
      });
      return { list, rest: '' };
    }
    const list = rings.map((c, k) => ({ ...items[k]!, mark: cueMark(c), layout: at(c) }));
    return { list, rest };
  }

  /** The step has callouts: a ring with words, or (a phone story) a sentence card. */
  private hasCallouts(stage: Stage): boolean {
    if (this.cardLinesOf(stage.step).length) return true;
    return stage.cuePlanFor(null).cues.some((c) => cueMark(c).effect !== 'look');
  }

  /** Muted Watch: which callout is showing `ms` into the step (the schedule `mutedStepMs` sums). */
  private calloutAt(
    stage: Stage,
    list: { text: string; mark: Cue | null; layout: Layout }[],
    ms: number,
  ): number {
    let t = 0;
    let at = stage.liveLayout();
    for (let k = 0; k < list.length; k++) {
      const to = this.calloutView(stage, at, list[k]!);
      t += this.autoHold(list[k]!.text) + (sameLayout(at, to) ? 0 : this.moveMs(at, to));
      if (ms < t) return k;
      at = to;
    }
    return Math.max(0, list.length - 1);
  }

  /** Muted Watch: how long a callout's card stays, finding it and reading its sentence. */
  private autoHold(text: string): number {
    return calloutHoldMs(text);
  }

  /**
   * Muted Watch plays a step's callouts itself, one card at a time, then the instruction with
   * what they did not show (founder, 2026-10-16: the whole paragraph in the tooltip and the ring
   * labels at once, "they overlap with tutorial"). The time all of that takes, for the step's
   * dwell; null when the step has no callouts or a voice is heard.
   */
  private mutedStepMs(step: Step): number | null {
    // A showcase plays its lines at a silent reader's pace.
    if (step.showcase && this.audio.narrationMs(step) == null) {
      return showcasePlan(step as ShowcaseStep, null, this.showcasePhone(step)).ms;
    }
    const stage = this.stage;
    if (!this.watch.playing || stage.step.id !== step.id || !stage.cued) return null;
    if (this.audio.narrationMs(step) != null) return null;
    if (!step.narration?.text.trim()) return null;
    if (!this.hasCallouts(stage)) return null;
    const { list, rest } = this.calloutPlan(stage);
    let total = 0;
    let at = stage.liveLayout();
    for (const it of list) {
      total += this.autoHold(it.text);
      // The camera moves only where `showCallout` moves it (the ring is not readable where it is).
      const to = this.calloutView(stage, at, it);
      if (!sameLayout(at, to)) total += this.moveMs(at, to);
      at = to;
    }
    // A phone story shows the tooltip as the headline the whole step: nothing left to read.
    const tip = step.hotspots.length && !this.told() ? (step.annotation?.text.trim() ?? '') : '';
    const left = [tip, rest.trim()].filter(Boolean).join(' ');
    if (left) total += this.autoHold(left);
    return Math.round(total);
  }

  /** A card comes after the last callout: the step's instruction, or its guide card. */
  private calloutsInstruction(): boolean {
    const step = this.current;
    if (step.hotspots.length) return !this.told() && !!step.annotation?.text.trim();
    return !this.calloutsEndStep();
  }

  /**
   * Where the camera is for a callout, coming from `from`: where it is when the whole ring is on
   * screen at nearly the zoom its framing asks for (readable here), else the callout's framing.
   * `showCallout` and muted Watch's timing (`mutedStepMs`) ask this one question.
   */
  private calloutView(
    stage: Stage,
    from: Layout,
    it: { mark: Cue | null; layout: Layout },
  ): Layout {
    // A sentence that lights nothing: the camera stays.
    if (!it.mark) return from;
    const vp = this.vp();
    const seen =
      rectOnScreen(it.mark.rect, from, { x: 0, y: 0, w: vp.w, h: vp.h }, stage.asset) &&
      from.scale >= it.layout.scale * CALLOUT_ZOOM_NEAR;
    return seen ? from : it.layout;
  }

  /** Light callout `k`: the camera to its framing when the ring is not readable here, then its card. */
  private showCallout(k: number): void {
    const co = this.co;
    if (!co || co.stage !== this.stage) return;
    // Laid out for a box the player no longer has: opened again on this callout at its size.
    if (this.fresh(k)) return;
    window.clearTimeout(co.timer);
    // Watch: the card keeps its count while the camera moves on; the next one takes it over. The
    // card already lit with its Next as the step opened keeps it too.
    const cards = this.told();
    this.dropCard(co.auto || (cards && co.i === k));
    const { stage } = co;
    const it = co.list[k]!;
    co.i = k;
    const vp = this.vp();
    const from = stage.liveLayout();
    // Readable where it is: the whole ring on screen, at nearly the zoom its framing asks for.
    const to = this.calloutView(stage, from, it);
    const ms = sameLayout(from, to) ? 0 : this.moveMs(from, to);
    const cam = ms > 0 ? { ms, ease: EASE_INOUT } : null;
    const ride = this.trackOverlay(stage, to, cam, false);
    stage.setCamera(to, cam);
    ride?.();
    const dim = spotColor(
      this.doc.theme.background,
      stage.framed ? undefined : DESKTOP_CUE_DIM_ALPHA,
    );
    // A phone story lights a part by the dim alone, no brown frame over the app's own.
    co.node = it.mark ? stage.showCue(it.mark, dim, !cards) : null;
    // A ring in the chrome bar's band: the bar steps aside while it is lit.
    const ring = it.mark ? projectRect(it.mark.rect, to, stage.asset) : null;
    this.chrome.setCueYield(!!ring && vp.h > 0 && ring.y + ring.h > vp.h - this.barBand());
    // The card comes once the camera is there: placed at rest, it never rides a move.
    // A phone story in click-through: the card is under the window, it never rides the move,
    // so it lights with the press (Watch keeps the move in its clock).
    if (cards && !co.auto) this.placeCard();
    else co.timer = window.setTimeout(() => this.placeCard(), ms);
  }

  /** The step goes straight on from the last callout: nothing to tap, nothing left to say. */
  private calloutsEndStep(): boolean {
    const step = this.current;
    if (!this.co || this.co.rest) return false;
    // A phone story's step with one way on ends the same as one with none (founder, 2026-10-22:
    // "Next step" on one step, a tap on a small ringed control on the next, was two ways for one
    // thing). The control stays tappable; the headline above the window still names it. Watch
    // playing the cards itself still taps the control.
    if (step.hotspots.length === 1 && this.told() && !this.co.auto) return true;
    return !step.hotspots.length && !step.annotation?.text.trim();
  }

  /**
   * The callout's card beside its ring with an arrow at it, on the side covering least of the
   * screen's words (`placeCallout`), inside the picture above the chrome bar, off the targets.
   * Focus goes to its Next when the keyboard was already in the player, so Enter goes on.
   */
  private placeCard(): void {
    const co = this.co;
    if (!co || co.stage !== this.stage || co.i < 0) return;
    const { stage } = co;
    const it = co.list[co.i]!;
    const last = co.i === co.list.length - 1;
    const room = this.overlay.labelRoom();
    const opts = {
      item: it,
      i: co.i + 1,
      // The instruction is the card after the last callout ("3 / 3").
      n: co.list.length + (this.calloutsInstruction() ? 1 : 0),
      last,
      // On the step's first card Back is the step before, when there is one.
      onBack:
        !co.auto && (co.i > 0 || this.history.length > 0)
          ? (via: CalloutVia) => this.cardBack(via)
          : undefined,
      // The last step's goes to the end card.
      next:
        last && this.calloutsEndStep()
          ? this.nextIdFor(stage.step)
            ? this.s.nextStep
            : this.s.finish
          : this.s.next,
      onNext: (via: CalloutVia) => this.calloutPress('next', via),
      onSkip: (via: CalloutVia) => this.calloutPress('skip', via),
      auto: co.auto,
      s: this.s,
    };
    // A phone story: the sentence is a card under the window and its buttons sit on it.
    const at = this.cardLinesOf(stage.step).indexOf(it.text);
    let card: HTMLElement | null = null;
    if (this.cards && at >= 0) {
      card = calloutFoot(opts);
      this.cards.dropFoot();
      this.cards.light(at, card);
    } else if (it.mark) {
      this.cards?.light(-1, null);
      card = calloutCard(opts);
      this.overlay.root.appendChild(card);
      co.card = card;
      this.placeFloating(card, stage, { mark: it.mark }, room);
    }
    if (co.auto) {
      // The clock turns the card (no event: nobody pressed anything); the last one hands over to
      // the instruction, or stays when the step goes straight on.
      if (!last || !this.calloutsEndStep()) {
        const k = co.i;
        co.timer = window.setTimeout(() => {
          if (this.co !== co || co.i !== k) return;
          if (last) this.closeCallouts();
          else this.showCallout(k + 1);
        }, this.autoHold(it.text));
      }
      return;
    }
    const focus = document.activeElement;
    if (focus && (focus === this.root || this.root.contains(focus))) {
      card?.querySelector<HTMLElement>('.mado-callout-next')?.focus({ preventScroll: true });
    }
  }

  /** The callout's card beside its ring, with its arrow (`placeCallout`), faded in. */
  private placeFloating(
    card: HTMLElement,
    stage: Stage,
    it: { mark: Cue },
    room: ReturnType<Overlay['labelRoom']>,
  ): void {
    const l = stage.currentLayout;
    const a = stage.asset;
    const pic = { x: l.tx, y: l.ty, w: a.w * l.scale, h: a.h * l.scale };
    const x0 = Math.max(pic.x, room.area.x);
    const y0 = Math.max(pic.y, room.area.y);
    const x1 = Math.min(pic.x + pic.w, room.area.x + room.area.w);
    const y1 = Math.min(pic.y + pic.h, room.area.y + room.area.h);
    const area = {
      x: x0 + 8,
      y: y0 + 8,
      w: Math.max(0, x1 - x0 - 16),
      h: Math.max(0, y1 - y0 - 16),
    };
    const box = card.getBoundingClientRect();
    const size = { w: box.width, h: box.height };
    const r = projectRect(it.mark.rect, l, a);
    const ring = { x: r.x - 4, y: r.y - 4, w: r.w + 8, h: r.h + 8 };
    const ink = (inkRects(a.ink) ?? []).map((n) => projectRect(n, l, a));
    // Beside its ring with an arrow at it, else (no side fits) where a label would go, no arrow.
    const p = placeCallout(ring, size, area, room.avoid, ink) ?? {
      ...placeLabel(ring, size, area, room.avoid, 12),
      side: null,
    };
    card.style.left = `${p.x}px`;
    card.style.top = `${p.y}px`;
    if (p.side) {
      card.classList.add(`mado-tooltip--${p.side}`);
      // The arrow aims at the middle of the span the card shares with its ring.
      const across = p.side === 'below' || p.side === 'above';
      const [at, len, from, span] = across
        ? [p.x, size.w, ring.x, ring.w]
        : [p.y, size.h, ring.y, ring.h];
      const mid = (Math.max(at, from) + Math.min(at + len, from + span)) / 2 - at;
      card.style.setProperty(
        across ? '--mado-notch' : '--mado-notch-y',
        `${Math.max(22, Math.min(mid, len - 22))}px`,
      );
    }
    reflow(card);
    card.classList.add('mado-tooltip--in');
  }

  /** Take the card away, keeping the keyboard in the player when it had the focus. */
  private dropCard(keepFoot = false): void {
    const co = this.co;
    const card = co?.card;
    if (!keepFoot && this.cards?.dropFoot()) this.root.focus({ preventScroll: true });
    if (!co || !card) return;
    co.card = null;
    const had = card.contains(document.activeElement);
    card.remove();
    if (had) this.root.focus({ preventScroll: true });
  }

  /**
   * A press on a callout. `next` (its button, ArrowRight/Enter/Space, a tap on a step with no
   * target): the next callout, after the last the instruction. `back` (ArrowLeft past the
   * first): the one before. `skip`: straight to the instruction. One `callout` event a press
   * (rule 4); the last callout's "Next step" is the step change itself, only its step events.
   */
  private calloutPress(act: 'next' | 'back' | 'skip', via: CalloutVia): void {
    const co = this.co;
    if (!co || co.auto || co.i < (act === 'back' ? 1 : 0)) return;
    // A pointer's press right after a step change is the tail of a double click.
    if (via !== 'key' && this.tapGuard) return;
    const last = co.i === co.list.length - 1;
    if (act === 'next' && last && this.calloutsEndStep()) return this.stepBy(1);
    this.analytics.emit('callout', this.current.id, { act, i: co.i + 1, n: co.list.length, via });
    if (act === 'back') this.showCallout(co.i - 1);
    // Skip lands on the step's last card, whose button is the way on, when that is how it ends.
    else if (act === 'skip' && this.calloutsEndStep()) this.showCallout(co.list.length - 1);
    else if (act === 'skip' || last) this.closeCallouts();
    else this.showCallout(co.i + 1);
  }

  /**
   * Back on a card (its button, ArrowLeft; founder, 2026-10-15: "move back either with keys or
   * arrows (not to go to another page, but next step back or forth)"). Within the step first: a
   * callout goes to the one before; the instruction to the last callout. From the step's first
   * card, the step before, opened on its last card. One event a press: a `callout` back within
   * the step (`i` = n + 1 from the instruction), the step change's own events when it leaves.
   */
  private cardBack(via: CalloutVia): void {
    if (via !== 'key' && this.tapGuard) return;
    const co = this.co;
    if (co && co.i > 0) return this.calloutPress('back', via);
    const left = this.sayLeft;
    if (!co && left && left.id === this.current.id && left.n > 0) {
      this.analytics.emit('callout', this.current.id, {
        act: 'back',
        i: left.n + 1,
        n: left.n,
        via,
      });
      this.overlay.removeTooltip();
      this.openCallouts(this.stage, 0, left.n - 1);
      return;
    }
    const prev = this.history[this.history.length - 1];
    if (!prev) return;
    this.landLast = prev;
    this.stepBy(-1);
  }

  /**
   * After the callouts: the step's instruction, its line now only the sentences no callout
   * showed. The camera goes to the target's framing only when a target is out of view.
   */
  private closeCallouts(): void {
    const co = this.co;
    if (!co) return;
    const { stage } = co;
    this.stopCallouts();
    this.holdLastCta();
    this.sayLeft = { id: stage.step.id, text: co.rest, n: co.list.length };
    const from = stage.liveLayout();
    const vp = this.vp();
    const screen = { x: 0, y: 0, w: vp.w, h: vp.h };
    const seen = stage.step.hotspots.every((h) => rectOnScreen(h.geom, from, screen, stage.asset));
    const to = seen ? from : stage.targetLayout();
    const ms = sameLayout(from, to) ? 0 : this.moveMs(from, to);
    this.overlay.render(stage.step, to, stage.asset, { settleMs: ms, keepEntrance: true });
    if (ms > 0) {
      const move = { ms, ease: EASE_INOUT };
      const ride = this.overlay.follow({ cam: { from, to, transition: cameraTransition(move) } });
      stage.setCamera(to, move);
      ride();
    }
    this.settleUntil = performance.now() + ms;
    this.walkUntil = this.settleUntil;
  }

  /** The callouts end without a press (a step change, Watch, a resize, the end): nothing left. */
  private stopCallouts(): void {
    const co = this.co;
    if (!co) return;
    window.clearTimeout(co.timer);
    this.dropCard();
    this.cards?.light(-1, null);
    this.co = null;
    if (co.node) co.stage.hideCue(co.node);
    this.overlay.callouts = false;
    this.ringLit(false);
    this.chrome.setCueYield(false);
  }

  /**
   * A silent walk: draw the step's targets and tooltip at `to`, where the camera
   * goes next, at once and shown. With `move`, they ride the camera there from
   * where the picture is now (`Overlay.follow`); the returned function starts
   * that ride and must run in the same task as the camera's own move.
   */
  private trackOverlay(
    stage: Stage,
    to: Layout,
    move: CameraMove | null,
    last = true,
  ): (() => void) | null {
    if (stage !== this.stage) return null;
    // A click on a type target is typing out: a redraw would cancel it and the advance it ends in.
    if (this.overlay.typingNow) return null;
    const from = stage.liveLayout();
    this.overlay.render(stage.step, to, stage.asset, {
      settleMs: 0,
      keepEntrance: true,
      instant: true,
      // A step's own typing starts once, where the walk ends.
      noTyping: !last,
    });
    if (!move || sameLayout(from, to)) return null;
    return this.overlay.follow({ cam: { from, to, transition: cameraTransition(move) } });
  }

  /** Forget the cue marks still to come (a new step, a skip). */
  private stopCues(): void {
    for (const t of this.cueTimers) clearTimeout(t);
    this.cueTimers = [];
    this.chrome.setCueYield(false);
    this.ringLit(false);
    this.stopCallouts();
  }

  /**
   * A narration ring is lit, or no longer: the control's pulse steps aside meanwhile, so two
   * things are never lit at once (click-through and muted Watch draw the control from the start).
   * The control stays where it is and still takes a tap.
   */
  private ringLit(on: boolean): void {
    this.overlay.root.classList.toggle('mado-overlay--ring', on);
  }

  /** Forget any walk in progress; the targets are live again. */
  private stopBeats(): void {
    for (const t of this.beatTimers) clearTimeout(t);
    this.beatTimers = [];
    this.waiting = false;
    this.liveWalk = false;
    this.overlay.root.classList.remove('mado-overlay--wait');
  }

  /** A camera move's length here: a phone's pace in the walkthrough, a desktop pan's elsewhere. */
  private moveMs(a: Layout, b: Layout): number {
    const vp = this.vp();
    return dur(this.walkZoom() !== undefined ? walkMoveDuration(a, b, vp) : panDuration(a, b, vp));
  }

  /**
   * Go to where the walk was going: the target, drawn and live at once, the camera moving
   * there at a pan's pace (a cut read as a glitch, 2026-10-14) with the targets riding the move.
   */
  private skipBeats(): void {
    if (!this.waiting) return;
    if (this.sc && this.current.showcase) {
      this.stopBeats();
      this.sc.finish();
      return;
    }
    this.stopBeats();
    this.stopCues();
    this.stage.clearCues();
    const from = this.stage.liveLayout();
    const to = this.stage.targetLayout();
    const ms = sameLayout(from, to) ? 0 : this.moveMs(from, to);
    // The step's entrance (a reveal, a slide) may still be running: the
    // targets keep following it.
    this.overlay.render(this.current, to, this.stage.asset, {
      settleMs: 0,
      keepEntrance: this.busy,
    });
    if (ms > 0) {
      const move = { ms, ease: EASE_INOUT };
      const ride = this.overlay.follow({ cam: { from, to, transition: cameraTransition(move) } });
      this.stage.setCamera(to, move);
      ride();
      this.watch.cameraPan(ms, EASE_INOUT);
    } else {
      this.stage.setCamera(to);
    }
    this.settleUntil = performance.now() + ms;
    this.walkUntil = this.settleUntil;
  }

  /**
   * Watch stopped without taking the viewer anywhere (Pause, Esc, a hidden tab): the walk
   * lands on its control rather than playing on silently with the targets hidden, and the
   * bubble the voice had replaced comes back (2026-10-14). A press that took over is left alone: the
   * walk was already skipped for it where it should be (`bindTakeover`), and redrawing the
   * targets now would take its own target from under it.
   */
  private watchStopped(why: StopReason): void {
    this.watchPlan = null;
    if (why === 'end') return;
    if (why !== 'pointer' && this.waiting) this.holdBeats();
    else if (!this.waiting) {
      // The camera was done but rings were still to come at the line's pace (Watch muted): a
      // stop takes them, and their words, away too.
      this.stopCues();
      this.stage.fadeCues();
    }
    this.overlay.restoreTooltip(this.current, this.stage.currentLayout, this.stage.asset);
    this.avoidCta();
  }

  /**
   * A pause is not a move: the walk stops where the picture is, and the camera stays (it never
   * rests, founder 2026-10-15). The targets are drawn and live at the current framing when all
   * of them are in view; else the viewer needs the glide to reach one (`skipBeats`).
   */
  private holdBeats(): void {
    if (!this.waiting) return;
    // A showcase holds where it is; Play goes on from there (`startWatch`).
    if (this.sc && this.current.showcase) {
      this.stopBeats();
      this.sc.stop();
      return;
    }
    const live = this.stage.liveLayout();
    const vp = this.vp();
    const screen = { x: 0, y: 0, w: vp.w, h: vp.h };
    const targets = this.current.hotspots.map((h) => h.geom);
    const seen =
      targets.length > 0 && targets.every((g) => rectOnScreen(g, live, screen, this.stage.asset));
    if (!seen) return this.skipBeats();
    this.stopBeats();
    this.stopCues();
    this.stage.fadeCues();
    // Freeze a move still running: the timers that would have ended it are gone.
    this.stage.setCamera(live);
    this.overlay.render(this.current, live, this.stage.asset, {
      settleMs: 0,
      keepEntrance: true,
      instant: true,
    });
    this.settleUntil = performance.now();
    this.walkUntil = this.settleUntil;
  }

  /**
   * The first step's walk, once the viewer is looking (after the intro cover, if any); or, with
   * `watching`, the step on screen's walk re-run for Watch (`watchWalk`). `glide`: the camera
   * moves to the opening framing instead of cutting to it, and the walk (and the voice) start
   * when it is there.
   */
  private openStory(watching = false, glide = false): void {
    this.reclaimFocus();
    // A seek: the walk starts part-way through, from where the camera is at that moment.
    const off = watching ? this.seekOffset : 0;
    this.seekOffset = 0;
    if (off > 0) glide = false;
    // The walk is planned from the stage's layout: the box it was made for, not an old one.
    this.fresh();
    const beats = this.stage.beats;
    if (!this.stage.cued && (!beats || beats.length < 2)) return;
    this.stopBeats();
    this.stopCues();
    this.stage.clearCues();
    let lead = 0;
    if (beats) {
      const from = this.stage.liveLayout();
      const to = beats[0]!.layout;
      lead = glide && !sameLayout(from, to) ? this.moveMs(from, to) : 0;
      this.stage.setCamera(to, lead > 0 ? { ms: lead, ease: EASE_INOUT } : null);
      if (lead > 0) this.watch.cameraPan(lead, EASE_INOUT);
    }
    const arrives = Math.max(0, this.playBeats(this.stage, lead - off, watching));
    if (watching) this.watchPlan = this.stage;
    this.walkUntil = performance.now() + arrives;
    // Watch mode speaks over the opening's whole screen (as `goTo`).
    this.settleUntil = watching ? performance.now() + lead : this.walkUntil;
    // Drawn where the walk ends, not on the opening shot the camera is on
    // now: nothing redraws the overlay when the walk arrives (as `paint`). Callouts: where the
    // camera is, each callout's move takes the targets with it (`showCallout`).
    this.overlay.render(
      this.current,
      this.co ? this.stage.currentLayout : this.stage.targetLayout(),
      this.stage.asset,
      { settleMs: arrives },
    );
  }

  destroy(): void {
    this.gateClose?.();
    this.sc?.destroy();
    this.scrub?.destroy();
    this.stopBeats();
    this.stopCues();
    if (this.tapGuard) window.clearTimeout(this.tapGuard);
    if (this.endTimer) window.clearTimeout(this.endTimer);
    if (this.ctaTimer) window.clearTimeout(this.ctaTimer);
    this.watchGone = true;
    this.watch.destroy();
    this.audio.destroy();
    this.presenter.destroy();
    this.ro?.disconnect();
    this.dockRo?.disconnect();
    if (this.onWinResize && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onWinResize);
      window.visualViewport?.removeEventListener('resize', this.onWinResize);
    }
    this.io?.disconnect();
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    this.analytics.flush(true);
    this.root.remove();
  }

  /** Watch mode is playing — exposed for the harness and tests. */
  get playing(): boolean {
    return this.watch.playing;
  }

  /** Exposed for the dev harness and editor preview. */
  get events(): Analytics {
    return this.analytics;
  }
}
